/* Ostrich Designs — theme.js
   Vanilla JS, progressive enhancement only. No external libraries.
   Facets, cart and the clean pill all work with JS disabled; this
   layer removes full page reloads where it can. */

(function () {
  "use strict";

  /* Conversational AI search worker (ostrich-search/). POST { query },
     see README there for the full response contract. Fixed infra, not a
     theme setting - CORS on the worker side is locked to the storefront's
     own domains regardless. A missing tier defaults to "clean" on the
     Worker (parseTierPreference), so we send "rude" below on purpose. */
  var AI_SEARCH_URL = "https://ostrich-search.alan-0a3.workers.dev/search";
  var AI_SEARCH_TIMEOUT_MS = 10000; /* Worker only; the Turnstile token has its own clock */
  var AI_SEARCH_MAX_QUERY_LENGTH = 200;

  /* Customer photo upload worker (ostrich-upload/), plan W5b. Same fixed-
     infra convention as AI_SEARCH_URL above - CORS on the worker side is
     locked to the store's own domains (brands/ostrich/storefront.json:
     .com and .co.uk, each with www) regardless of this constant.
     Live (verified via GET /health, 2026-09-20) at the workers.dev url
     below; a future custom route means updating this by hand. */
  var UPLOAD_URL = "https://ostrich-upload.alan-0a3.workers.dev/upload";
  var UPLOAD_MAX_BYTES = 15 * 1024 * 1024;

  /* Same worker, the /offer route (mints a one-time 10% code for a buyer
     who has just shared a photo through UPLOAD_URL - never a review
     reward, see ostrich-upload/src/offer.ts). Used only by
     initHelloOffer() below, on the /pages/hello page the mug-box insert
     card's QR code points at. */
  var OFFER_URL = "https://ostrich-upload.alan-0a3.workers.dev/offer";

  /* Same worker, the /email-code route (sends a copy of an already-claimed
     /offer code to an address the buyer types in). Used only by
     initHelloOffer() below. */
  var EMAIL_CODE_URL = "https://ostrich-upload.alan-0a3.workers.dev/email-code";

  /* ---------- Shared: locale strings + product-card rendering ----------
     Both the AI search results panel and the taxonomy-driven
     recommendations fetch (below) render product cards
     client-side by cloning [data-product-card-template] (see
     sections/header.liquid) and filling named [data-field] slots via
     textContent/attribute assignment only - never innerHTML - so nothing
     from a Worker or Storefront API response is ever parsed as HTML.
     This is the one implementation both features share, so a client-
     rendered card always matches snippets/product-card.liquid's markup. */

  var ostrichStrings = null;
  function getOstrichStrings() {
    if (ostrichStrings) return ostrichStrings;
    ostrichStrings = {};
    var el = document.querySelector("[data-ostrich-strings]");
    if (el) {
      try {
        ostrichStrings = JSON.parse(el.textContent) || {};
      } catch (e) {
        ostrichStrings = {};
      }
    }
    return ostrichStrings;
  }

  function safeJsonParse(raw) {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  /* Deterministic 1-6 card colour-block index from a product handle, for
     visual parity with the grid's own assignment (snippets/card-color
     -index.liquid, an md5-of-handle hash). This is a plain character-sum
     hash, not MD5 - close enough for a decorative background block picked
     from a handful of near-identical low-saturation tones, and avoids
     shipping an MD5 implementation into theme.js for that alone. */
  function hashHandle(handle) {
    var str = String(handle || "");
    var sum = 0;
    for (var i = 0; i < str.length; i++) sum += str.charCodeAt(i);
    return (sum % 6) + 1;
  }

  function formatMoney(amount, currencyCode) {
    var num = parseFloat(amount);
    if (isNaN(num) || !currencyCode) return "";
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-GB", {
        style: "currency",
        currency: currencyCode,
      }).format(num);
    } catch (e) {
      return currencyCode + " " + num.toFixed(2);
    }
  }

  /* fields: { handle, url, tier, imageUrl, imageUrl2, title, price, currencyCode }
     opts.deferImage: withholds the main image src (sets data-src instead,
     and drops the hover-swap image entirely) - mirrors snippets/product
     -card.liquid's defer_image param. No current caller passes this (the
     cross-lane panel that used to pass it was removed with the Dial); left
     in place because product-card.liquid's own defer_image param is still
     live, and a client-rendered card must keep matching it. */
  function buildProductCard(fields, opts) {
    opts = opts || {};
    var template = document.querySelector("[data-product-card-template]");
    if (!template || !template.content) return document.createDocumentFragment();
    var frag = template.content.cloneNode(true);
    var a = frag.querySelector("a.product-card");
    var tier = fields.tier === "banter" || fields.tier === "rude" ? fields.tier : "clean";
    if (tier === "rude" && toneSwitchOff()) return document.createDocumentFragment();
    var title = fields.title || "";

    a.href = fields.url || "#";
    a.setAttribute("data-tier", tier);
    a.setAttribute("data-card", String(hashHandle(fields.handle)));
    /* data-spin: same attribute snippets/product-card.liquid emits, same
       consumer (initCardSpinHover). Not set for a deferred card -- that
       path already drops the still-image rollover for the same reason
       (the card is not visible yet, so no request should be armed for it
       either) -- or when the field is missing (no gif, or the gif fell
       outside images(first: 3)). Width matches the still images' own
       `image_url: width: 700` so the two never crossfade at different
       intrinsic sizes; Shopify's CDN accepts `&width=` on ANY of its
       urls, staged upload or Storefront `images` connection alike. */
    if (!opts.deferImage && fields.spinUrl) {
      var sep = fields.spinUrl.indexOf("?") !== -1 ? "&" : "?";
      a.setAttribute("data-spin", fields.spinUrl + sep + "width=700");
    }
    /* NO aria-label HERE, matching snippets/product-card.liquid: this same
       function also fills price/badge fields below, and an aria-label
       naming only the title mismatched that visible content (axe
       label-content-name-mismatch). No override -> no mismatch. */

    var img = frag.querySelector('[data-field="image"]');
    var imgAlt = frag.querySelector('[data-field="image-alt"]');
    if (opts.deferImage) {
      if (imgAlt) imgAlt.remove();
      if (img) {
        if (fields.imageUrl) {
          img.setAttribute("data-src", fields.imageUrl);
          img.alt = title;
        } else {
          img.remove();
        }
      }
    } else {
      if (img) {
        if (fields.imageUrl) {
          img.src = fields.imageUrl;
          img.alt = title;
        } else {
          img.remove();
        }
      }
      if (imgAlt) {
        if (fields.imageUrl2) {
          imgAlt.src = fields.imageUrl2;
        } else {
          imgAlt.remove();
        }
      }
    }

    /* ONE NEUTRAL CARD (2026-09-19): ONE badge, matching snippets/card
       -badge.liquid's own join exactly ("New", "Add a name", or both
       joined with " · ") -- never the old two stacked chips. */
    var badgeEl = frag.querySelector('[data-field="badge"]');
    if (badgeEl) {
      var strings = getOstrichStrings();
      var days = Number(strings.newBadgeDays) || 5;
      var made = fields.createdAt ? Date.parse(fields.createdAt) : NaN;
      var isNew = !isNaN(made) && (Date.now() - made) < days * 86400000;
      var badgeParts = [];
      if (isNew) badgeParts.push(strings.badgeNew || "New");
      if (fields.personalisable) badgeParts.push(strings.badgePersonalise || "Add a name");
      if (badgeParts.length) {
        badgeEl.textContent = badgeParts.join(" · ");
        badgeEl.hidden = false;
      } else {
        badgeEl.hidden = true;
      }
    }

    var captionEl = frag.querySelector('[data-field="caption"]');
    if (captionEl) captionEl.textContent = title;

    /* MASK-RESEARCH-2026-09-20 section 2(b): the mask markup and
       its copy are already in data-product-card-template (sections/header
       .liquid), rendered server-side by Liquid inside the <template> and
       hidden by default -- unhide it for a rude-tier result instead of
       building a second copy, so a client-rendered card can never drift
       out of step with snippets/product-card.liquid's own SSR one. */
    var maskEl = frag.querySelector(".card-mask");
    if (maskEl) {
      if (tier === "rude") {
        maskEl.hidden = false;
      } else {
        maskEl.remove();
      }
    }

    var priceEl = frag.querySelector('[data-field="price"]');
    if (priceEl) {
      var money = fields.price != null ? formatMoney(fields.price, fields.currencyCode) : "";
      if (money) {
        priceEl.textContent = money;
      } else {
        priceEl.remove();
      }
    }

    /* CARD PARITY (2026-09-19, "search is a real page", audit item 5): set
       the SAME data-* attributes snippets/delivery-date.liquid renders
       server-side, then hand off to initDeliveryDates()'s own tick - one
       date-arithmetic implementation, not a second one here. Only when the
       caller actually supplied delivery data (dispatchPromise/stockOnHand/
       personalisable) - a caller that hasn't been updated to pass these
       yet (there is none left after this change, but a future one could
       exist) gets the element removed rather than a silently-wrong
       generic estimate. */
    var deliveryEl = frag.querySelector('[data-delivery-date]');
    /* A product CARD carries no delivery line (Alan, 2026-09-25, on the
       catchphrases collection: "drop the get it date and order by, the ones
       on the product page are far better"). snippets/product-card.liquid
       stopped rendering it that day; the client-built cards (search,
       recommendations) reach the same rule here. */
    if (deliveryEl && deliveryEl.classList.contains("delivery-date--card")) { deliveryEl.remove(); deliveryEl = null; }
    if (deliveryEl) {
      if (fields.dispatchPromise != null || fields.stockOnHand != null || fields.personalisable != null) {
        var deliveryAttrs = computeDeliveryDateAttrs(fields);
        deliveryEl.setAttribute("data-dispatch-days", String(deliveryAttrs.dispatchDays));
        deliveryEl.setAttribute("data-transit-min", String(deliveryAttrs.transitMin));
        deliveryEl.setAttribute("data-transit-max", String(deliveryAttrs.transitMax));
        deliveryEl.setAttribute("data-cutoff-hour", String(deliveryAttrs.cutoffHour));
        if (deliveryAttrs.recordedTier) deliveryEl.setAttribute("data-tier", deliveryAttrs.recordedTier);
        var deliveryStrings = getOstrichStrings();
        var fallbackTemplate = deliveryStrings.dispatchedIn || "Dispatched in __DAYS__ working days";
        deliveryEl.textContent = fallbackTemplate.replace("__DAYS__", String(deliveryAttrs.dispatchDays));
        deliveryEl.hidden = false;
      } else {
        deliveryEl.remove();
      }
    }

    return frag;
  }

  /* Mirrors snippets/delivery-date.liquid's own {% case %}/stock-fallback/
     threshold logic exactly (see that snippet's header comment for the
     table both sides must agree on), reading the same theme settings via
     data-ostrich-strings (header.liquid) and the same shipping-threshold
     facts #CartDrawer already carries (snippets/cart-drawer.liquid's own
     "DATA, NOT LITERALS" comment - same convention, not a new one) rather
     than duplicating either as a third copy. Liquid/JS still can't share
     code, so this is a second hand-written copy of the SAME table
     delivery-date.liquid documents - not a new duplication, an existing
     one now also serving client-built cards. */
  // bench 0 since 2026-09-21 (same-day); this copy still said 1 until
  // 2026-09-29. The Liquid side is snippets/dispatch-days.liquid.
  // supplier_6d since 2026-10-21: a blank whose supplier is slower (the black
  // 11oz mug), common/lead_time.py::DISPATCH_DAYS_BY_KEY.
  var DISPATCH_DAYS_BY_RECORDED_TIER = { shelf: 0, bench: 0, supplier: 4, supplier_6d: 6 };
  function computeDeliveryDateAttrs(fields) {
    var strings = getOstrichStrings();
    var dispatchDays;
    var recordedTier = null;
    if (Object.prototype.hasOwnProperty.call(DISPATCH_DAYS_BY_RECORDED_TIER, fields.dispatchPromise)) {
      recordedTier = fields.dispatchPromise;
      dispatchDays = DISPATCH_DAYS_BY_RECORDED_TIER[recordedTier];
    } else {
      var inStock = Number(fields.stockOnHand) > 0;
      dispatchDays = inStock
        ? (Number(strings.dispatchDaysInStock) || 1)
        : (Number(strings.dispatchDaysMadeToOrder) || 4);
      if (fields.personalisable) dispatchDays += Number(strings.personalisedExtraDays) || 1;
    }

    var transitMin = 2, transitMax = 3;
    var cartDrawer = document.getElementById("CartDrawer");
    if (cartDrawer) {
      var freeAll = cartDrawer.getAttribute("data-shipping-free-all") === "true";
      var thresholdMinor = parseInt(cartDrawer.getAttribute("data-shipping-threshold") || "", 10);
      if (freeAll && !isNaN(thresholdMinor) && thresholdMinor > 0) {
        var priceMinor = Math.round((parseFloat(fields.price) || 0) * 100);
        if (priceMinor >= thresholdMinor) { transitMin = 1; transitMax = 1; }
      }
    }

    return {
      dispatchDays: dispatchDays,
      transitMin: transitMin,
      transitMax: transitMax,
      recordedTier: recordedTier,
      cutoffHour: Number(strings.dispatchCutoffHour) || 14,
    };
  }

  /* ---------- Mobile nav ---------- */

  /* ---------- "Get it <day> to <day>": working-day arithmetic for
     delivery-date ----------
     Reads the dispatch/transit/cut-off figures the snippet put on the
     element (dispatch days from the product's OWN recorded promise when
     `ostrich-theme/snippets/delivery-date.liquid` found one, else its
     settings-only fallback; transit is Royal Mail Tracked 48's fixed 2-3
     working days), counts working days from today (tomorrow if past the
     cut-off), skips weekends, and writes a RANGE, not one far date -- see
     that snippet's own header for the "Get it by Wed 30 Sept" bug this
     replaced and the table both sides must agree on. The Liquid fallback
     text stays if anything is missing. Bank holidays are not known here;
     the day after one reads a day early, the honest direction to be wrong
     in for a made-to-order product.

     A sibling `[data-order-countdown]` element, when present, is filled
     with "Order within Nh Nm": the time to the NEXT cut-off that is still
     ahead on a working day -- the exact instant the "Get it" range beside
     it will roll (common/lead_time.py::next_cutoff, same rule). It never
     blanks: after 14:00 it counts to tomorrow's cut-off, on a Friday
     afternoon or a weekend to Monday's, and on every tier alike. */
  /* THE DAYS WE DESPATCH (Alan, 2026-09-25: "we will ship on saturday
     until 2pm"): read once off <body data-dispatch-days> (the theme setting
     dispatch_days_of_week, JS getDay numbers), the literal copy of
     common/lead_time.py::DISPATCH_WEEKDAYS. Transit (addWorkingDays) still
     counts Royal Mail's Monday-Friday, and normalises a Saturday start onto
     Monday first, exactly as the Python add_working_days does. */
  var dispatchDaySet = null;
  /* SATURDAY'S CUT-OFF IS EARLIER (Alan, 2026-09-26: "change saturday cutoff
     to 10am across the site"): <body data-cutoff-hour-sat>, the mirror of
     common/lead_time.py SATURDAY_CUTOFF_HOUR / cutoff_hour_for(). Every
     time claim on the site asks this for the day it is talking about. */
  function cutoffHourFor(d, base) {
    if (d.getDay() === 6) {
      var sat = Number(document.body && document.body.getAttribute("data-cutoff-hour-sat"));
      if (!isNaN(sat) && sat > 0) return sat;
    }
    return base;
  }
  function isDispatchDay(d) {
    if (!dispatchDaySet) {
      dispatchDaySet = {};
      var raw = (document.body && document.body.getAttribute("data-dispatch-days")) || "1,2,3,4,5";
      raw.split(",").forEach(function (x) { var n = parseInt(x, 10); if (!isNaN(n)) dispatchDaySet[n] = true; });
    }
    return !!dispatchDaySet[d.getDay()];
  }
  function nextDispatchDay(d) {
    var x = new Date(d.getTime());
    while (!isDispatchDay(x)) x.setDate(x.getDate() + 1);
    return x;
  }
  /* THE DAYS THE CARRIER DELIVERS (Alan, 2026-09-25): <body
     data-delivery-days>, Monday to Saturday, the mirror of
     common/lead_time.py DELIVERY_WEEKDAYS. addDeliveryDays counts n such
     days strictly AFTER the despatch day, never normalising it: Saturday
     + 1 is Monday (express), Saturday + 2 is Tuesday (standard). */
  var deliveryDaySet = null;
  function isDeliveryDay(d) {
    if (!deliveryDaySet) {
      deliveryDaySet = {};
      var rawD = (document.body && document.body.getAttribute("data-delivery-days")) || "1,2,3,4,5";
      rawD.split(",").forEach(function (x) { var n = parseInt(x, 10); if (!isNaN(n)) deliveryDaySet[n] = true; });
    }
    return !!deliveryDaySet[d.getDay()];
  }
  function addDeliveryDays(start, n) {
    var d = new Date(start.getTime());
    while (n > 0) {
      d.setDate(d.getDate() + 1);
      if (isDeliveryDay(d)) n -= 1;
    }
    return d;
  }
  function addWorkingDays(start, n) {
    var d = new Date(start.getTime());
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
    while (n > 0) {
      d.setDate(d.getDate() + 1);
      var wd = d.getDay();
      if (wd !== 0 && wd !== 6) n -= 1;
    }
    return d;
  }

  /* Set once the recurring tick is armed, so a second call (made whenever
     new cards with their own [data-delivery-date] are inserted after the
     page's own DOMContentLoaded pass - initSearchResultsEnhance() and
     renderRecommendationSection() below) refreshes those new elements
     immediately without arming a second, redundant setInterval. */
  var deliveryDatesTickerArmed = false;
  function initDeliveryDates() {
    if (!document.querySelector("[data-delivery-date]")) return;
    var strings = getOstrichStrings();
    var rangeTemplate = strings.getItRange || "Get it __LOW__ to __HIGH__";
    var onTemplate = strings.getItOn || "Get it __DATE__";
    var countdownTemplate = strings.orderCountdown || "Order within __TIME__";
    var dayFmt, dayMonthFmt, longFmt, usShortFmt;
    try {
      dayFmt = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric" });
      dayMonthFmt = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short" });
      longFmt = new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long" });
      /* US MARKET (2026-10-15): en-US short dates ("Oct 27") for the
         [data-market="us"] element snippets/delivery-date.liquid and
         snippets/basket-delivery.liquid render -- never the GB formatters
         above, which stay exactly as they were for every other market. */
      usShortFmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
    } catch (e) { return; }
    /* The countdown is Amazon's shape (Alan, 2026-09-19: "i'd even like a
       countdown like amazon do"; 2026-09-21, on finding it blank at 15:06:
       "which was the way i asked for in the first place"): it ticks at every
       hour of every day, counting to the next cut-off that is still ahead
       on a working day, and says nothing about the day itself -- the "Get
       it" range beside it already does, and both roll at the same instant,
       because `start` below is exactly the day whose cut-off this counts to.
       Amazon's own line: "FREE delivery Tomorrow. Order within 4 hrs 12
       mins", then a minute past the cut-off "FREE delivery Wednesday. Order
       within 22 hrs 30 mins". */
    function countdownText(minutes, words) {
      var h = Math.floor(minutes / 60);
      var m = minutes % 60;
      if (words) {
        /* Amazon's own spelling on the product page: "4 hrs 12 mins". */
        if (h >= 24) { var d = Math.floor(h / 24); return d + (d > 1 ? " days " : " day ") + (h % 24) + " hrs"; }
        if (h > 0) return h + " hrs " + m + " mins";
        return m + " mins";
      }
      if (h >= 24) return Math.floor(h / 24) + "d " + (h % 24) + "h";
      if (h > 0) return h + "h " + m + "m";
      return m + "m";
    }
    /* Amazon's date on the product page: "Wednesday, 23 September", or for
       a range "Wednesday, 23 September to Thursday, 24 September". A card
       keeps the short "Get it Wed 23 to Thu 24 Sep". */
    function longRange(earliest, latest) {
      if (earliest.getTime() === latest.getTime()) return longFmt.format(earliest);
      return longFmt.format(earliest) + " to " + longFmt.format(latest);
    }
    function tick() {
      var now = new Date();
      // Re-queried every tick (was captured once, above, at the first
      // call) so a card inserted after this init first ran - a Worker-
      // ranked search result, a taxonomy recommendation - is picked up on
      // the very next 30s tick without needing its own setInterval; the
      // immediate tick() call below (a fresh call to initDeliveryDates()
      // right after such an insert) covers the instant before that.
      var els = document.querySelectorAll("[data-delivery-date]");
      els.forEach(function (el) {
        var dispatch = Number(el.getAttribute("data-dispatch-days"));
        var transitMin = Number(el.getAttribute("data-transit-min"));
        var transitMax = Number(el.getAttribute("data-transit-max"));
        var cutoff = Number(el.getAttribute("data-cutoff-hour"));
        if (isNaN(dispatch) || isNaN(transitMin) || isNaN(transitMax)) return;
        var pastCutoff = !isNaN(cutoff) && now.getHours() >= cutoffHourFor(now, cutoff);
        var start = new Date(now.getTime());
        if (pastCutoff) start.setDate(start.getDate() + 1);
        start = nextDispatchDay(start);
        /* A same-day tier posts on the start day itself, Saturday included;
           a tier with days to count counts them on the Monday-Friday
           calendar (addWorkingDays rolls a Saturday start to Monday first). */
        var dispatchDate = dispatch > 0 ? addWorkingDays(start, dispatch) : new Date(start.getTime());
        var earliest = addDeliveryDays(dispatchDate, transitMin);
        var latest = addDeliveryDays(dispatchDate, transitMax);
        var sameMonth = earliest.getMonth() === latest.getMonth();
        var low = sameMonth ? dayFmt.format(earliest) : dayMonthFmt.format(earliest);
        var high = dayMonthFmt.format(latest);
        var longStyle = el.getAttribute("data-date-style") === "long";
        var sameDay = earliest.getTime() === latest.getTime();
        /* US MARKET (2026-10-15): the only place that reads data-market.
           en-US short dates, "Arrives __DATE__" or "Arrives __LOW__ -
           __HIGH__" -- never the GB "Get it"/long-style sentence below,
           which is untouched and still runs for every element without
           data-market="us" (every other market, always). */
        if (el.getAttribute("data-market") === "us" && usShortFmt) {
          var usHigh = usShortFmt.format(latest);
          el.textContent = sameDay ? ("Arrives " + usHigh) : ("Arrives " + usShortFmt.format(earliest) + " - " + usHigh);
        } else {
          el.textContent = longStyle ? longRange(earliest, latest)
            : sameDay ? onTemplate.replace("__DATE__", high)
            : rangeTemplate.replace("__LOW__", low).replace("__HIGH__", high);
        }
        el.setAttribute("data-delivery-computed", "true");
        /* The fastest-delivery line (PDP only, under the Tracked 24
           threshold): Tracked 24 is one working day from dispatch. */
        var fastest = el.parentElement && el.parentElement.parentElement
          ? el.parentElement.parentElement.querySelector("[data-fastest-line]") : null;
        if (fastest) {
          if (!fastest.getAttribute("data-template")) fastest.setAttribute("data-template", fastest.textContent);
          fastest.textContent = fastest.getAttribute("data-template").replace("__DATE__", longFmt.format(addDeliveryDays(dispatchDate, 1)));
          fastest.hidden = false;
        }

        var countdownEl = el.parentElement
          ? el.parentElement.querySelector("[data-order-countdown]")
          : null;
        if (!countdownEl) return;
        var clockOnly = countdownEl.hasAttribute("data-clock-only");
        var clockWrap = clockOnly && el.parentElement ? el.parentElement.querySelector("[data-order-countdown-wrap]") : null;
        function hideClock() { countdownEl.hidden = true; if (clockWrap) clockWrap.hidden = true; }
        if (isNaN(cutoff)) { hideClock(); return; }
        /* `start` is already the working day whose cut-off is next: today
           before the cut-off, else rolled forward past the cut-off and any
           weekend above. */
        var cutoffAt = new Date(start.getTime());
        cutoffAt.setHours(cutoffHourFor(start, cutoff), 0, 0, 0);
        var minutesLeft = Math.ceil((cutoffAt.getTime() - now.getTime()) / 60000);
        if (minutesLeft < 1) { hideClock(); return; }
        countdownEl.textContent = clockOnly ? countdownText(minutesLeft, true)
          : countdownTemplate.replace("__TIME__", countdownText(minutesLeft));
        countdownEl.hidden = false;
        if (clockWrap) clockWrap.hidden = false;
      });
    }
    tick();
    if (!deliveryDatesTickerArmed) {
      deliveryDatesTickerArmed = true;
      setInterval(tick, 30000);
      // A block whose attributes changed (the basket's, after a redraw:
      // assets/basket-delivery.js) asks for a redraw now, not in 30s.
      document.addEventListener("ostrich:delivery-refresh", function () { tick(); });
    }
  }

  /* ---------- Personalisation: what the mug will say, and how much room is left ---------- */

  function initPersonalisationPreview() {
    var inputs = document.querySelectorAll("[data-personalisation-input]");
    if (!inputs.length) return;
    var charsLeft = getOstrichStrings().charsLeft || "__N__ left";
    inputs.forEach(function (input) {
      if (input.type !== "text") return;
      var wrap = input.parentElement;
      var live = wrap ? wrap.querySelector("[data-personalisation-live]") : null;
      if (!live) return;
      var preview = live.querySelector("[data-personalisation-preview]");
      var count = live.querySelector("[data-personalisation-count]");
      var max = Number(input.getAttribute("maxlength"));
      function update() {
        var v = input.value;
        if (!v) { live.hidden = true; return; }
        if (preview) preview.textContent = v;
        if (count && !isNaN(max) && max > 0) {
          count.textContent = charsLeft.replace("__N__", String(Math.max(0, max - v.length)));
        }
        live.hidden = false;
      }
      input.addEventListener("input", update);
      update();
    });
  }

  /* ---------- Rendered personalisation preview (2026-09-29) ----------
     Alan: "do the proper build on the mug previews" / "build on B". The page
     asks the relay (infra/preview) for the mug as it will print, drawn by the
     order's own renderer on the box. Tapping into the box warms that
     product's renderer and does the ONE Turnstile check, which returns a
     short session; each pause in typing (250 ms) asks for the image. A 202
     means still rendering (asked again, up to 3 times); a 422 means the
     renderer refused this text, so the image hides and the text echo stays.
     Phase 1 is one typed line per product. */
  function initLivePreview() {
    var fig = document.querySelector("[data-live-preview]");
    if (!fig) return;
    var endpoint = (fig.getAttribute("data-endpoint") || "").replace(/\/+$/, "");
    var productId = fig.getAttribute("data-product-id");
    var img = fig.querySelector("[data-live-preview-image]");
    // THE PLATE CLOSE-UP (2026-10-19): a second picture the edge answers as
    // plate_url for lines that have one; shown with the mug, hidden without.
    var plate = fig.querySelector("[data-live-preview-plate]");
    var plateCap = fig.querySelector("[data-live-preview-plate-caption]");
    var inputs = document.querySelectorAll('.personalise-note input[type="text"][data-personalisation-input]');
    if (!endpoint || !img || inputs.length !== 1) return;
    var input = inputs[0];
    // THE PLATE STYLE (2026-10-19): the UAE line's emirate pulldown, a
    // `dropdown` slot keyed plate_style. Sent with every ask so the edge keys
    // the cache on it and the renderer draws that emirate's plate; a product
    // without the pulldown sends nothing and asks exactly as before.
    var styleSelect = document.querySelector('.personalise-note select[data-slot-key="plate_style"]');
    var session = null, warming = null, timer = null, seq = 0;

    function plateStyle() {
      return styleSelect && styleSelect.value ? styleSelect.value : null;
    }
    function variantId() {
      var v = document.querySelector('#ProductForm [name="id"], [form="ProductForm"][name="id"]');
      return v && v.value ? v.value : null;
    }
    function post(path, body) {
      return fetch(endpoint + path, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body)
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (j) { j._status = res.status; return j; });
      });
    }
    function warm() {
      if (warming) return warming;
      warming = getTurnstileTokenFor("preview").then(function (token) {
        return post("/warm", { product_id: productId, turnstileToken: token });
      }).then(function (j) {
        if (j._status === 200 && j.session_token) { session = j.session_token; return session; }
        warming = null;
        return null;
      }).catch(function () { warming = null; return null; });
      return warming;
    }
    function ask(text, mine, attempt) {
      var body = { product_id: productId, variant_id: variantId(), text: text, session_token: session };
      var style = plateStyle();
      if (style) body.plate_style = style;
      post("/preview", body)
        .then(function (j) {
          if (mine !== seq) return;               // a newer keystroke owns the image
          if (j._status === 200 && j.url) {
            // SHOWN ONLY ONCE THE IMAGE HAS LOADED (Alan, 2026-09-29): an
            // empty frame with alt text read as broken. The new picture is
            // fetched off-page and swapped in when complete, so the previous
            // one stays up while the next arrives.
            var next = new Image();
            next.onload = function () {
              if (mine !== seq) return;
              img.src = j.url;
              fig.hidden = false;
            };
            next.src = j.url;
            if (plate) {
              if (j.plate_url) {
                var nextPlate = new Image();
                nextPlate.onload = function () {
                  if (mine !== seq) return;
                  plate.src = j.plate_url;
                  plate.hidden = false;
                  if (plateCap) plateCap.hidden = false;
                };
                nextPlate.src = j.plate_url;
              } else {
                plate.hidden = true;
                if (plateCap) plateCap.hidden = true;
              }
            }
          } else if ((j._status === 202 || j._status === 429) && attempt < 3) {
            setTimeout(function () { if (mine === seq) ask(text, mine, attempt + 1); }, j.retry_after_ms || 300);
          } else if (j._status === 401 && attempt < 1) {
            session = null; warming = null;       // the session expired: one fresh one
            warm().then(function (s) { if (s && mine === seq) ask(text, mine, attempt + 1); });
          } else if (j._status === 422) {
            fig.hidden = true;
          }
        })
        .catch(function () { /* network failure: the text echo stands */ });
    }
    function update() {
      var text = input.value.trim();
      var mine = ++seq;
      if (!text) { fig.hidden = true; return; }
      warm().then(function (s) { if (s && mine === seq) ask(text, mine, 0); });
    }
    function schedule() { clearTimeout(timer); timer = setTimeout(update, 250); }
    input.addEventListener("focus", function () { warm(); });
    input.addEventListener("input", schedule);
    // A different colour (variant) is a different mug, and so is a different
    // plate style: redraw if there is text.
    document.addEventListener("change", function (e) {
      if (e.target === styleSelect && input.value.trim()) { schedule(); return; }
      if (e.target !== input && e.target.closest && e.target.closest("#ProductForm, [form='ProductForm'], .variant-picker") && input.value.trim()) schedule();
    });
  }

  function initMobileNav() {
    var toggle = document.querySelector("[data-mobile-nav-toggle]");
    var nav = document.querySelector("[data-mobile-nav]");
    if (!toggle || !nav) return;
    toggle.addEventListener("click", function () {
      var open = nav.getAttribute("data-open") === "true";
      nav.setAttribute("data-open", open ? "false" : "true");
      toggle.setAttribute("aria-expanded", open ? "false" : "true");
    });
    // The panel is an in-page disclosure (it pushes the page down; it is not
    // modal), so the APG disclosure-navigation pattern applies: Escape
    // closes it and focus goes back to the Menu button. No trap (audit
    // 2026-09-29).
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape" || nav.getAttribute("data-open") !== "true") return;
      nav.setAttribute("data-open", "false");
      toggle.setAttribute("aria-expanded", "false");
      toggle.focus();
    });
  }

  /* ---------- "Show rude ones" switch ----------
     MASK-RESEARCH-2026-09-20.md section 2(c). Two real
     role="switch" buttons exist in the DOM (sections/header.liquid: one
     in the header row for desktop, one in the mobile-nav panel for
     phones), both carrying [data-rude-switch] so they move together.
     layout/theme.liquid's own inline script already set
     data-show-rude="true" on <html> before first paint when localStorage
     said so (and theme.css's bootstrap rule already painted the thumb
     "on" from that same attribute) -- this only wires the click and
     brings aria-checked into line with it. */
  var RUDE_SWITCH_KEY = "ostrich:show-rude";
  /* settings.tone_switch_enabled off (layout/theme.liquid stamps
     data-tone-switch="off" on <html>; Alan, 2026-10-19, for the .ae store:
     no switch, and the rude tier never shows). Nothing in this file may
     turn the rude tier on while it is off, whatever localStorage, a URL or
     a finder choice says; snippets/tier-guard.liquid already refused the
     cards server-side, so what is left here is belt and braces for a
     stale attribute or a card another script built. */
  function toneSwitchOff() {
    return document.documentElement.getAttribute("data-tone-switch") === "off";
  }
  function enforceToneSwitchOff() {
    document.documentElement.setAttribute("data-show-rude", "false");
    try { localStorage.removeItem(RUDE_SWITCH_KEY); } catch (e) {}
    document.querySelectorAll('.product-card[data-tier="rude"]').forEach(function (card) {
      var li = card.parentElement && card.parentElement.tagName === "LI" ? card.parentElement : card;
      li.remove();
    });
  }
  function setRudeSwitchesChecked(on) {
    document.querySelectorAll("[data-rude-switch]").forEach(function (btn) {
      btn.setAttribute("aria-checked", on ? "true" : "false");
    });
  }
  function initRudeSwitch() {
    setRudeSwitchesChecked(document.documentElement.getAttribute("data-show-rude") === "true");
    document.querySelectorAll("[data-rude-switch]").forEach(function (btn) {
      if (btn.hasAttribute("data-rude-switch-bound")) return;
      btn.setAttribute("data-rude-switch-bound", "true");
      btn.addEventListener("click", function () {
        var on = document.documentElement.getAttribute("data-show-rude") !== "true";
        document.documentElement.setAttribute("data-show-rude", on ? "true" : "false");
        setRudeSwitchesChecked(on);
        try {
          localStorage.setItem(RUDE_SWITCH_KEY, on ? "1" : "0");
        } catch (e) {}
      });
    });
  }


  /* ---------- Facets: enhance to avoid full reloads ---------- */

  function initFacets() {
    var form = document.querySelector("[data-facets-form]");
    var results = document.querySelector("[data-facets-results]");
    if (!form || !results) return;

    var facetsToggle = document.querySelector("[data-facets-toggle]");
    var facetsPanel = document.querySelector("[data-facets-panel]");
    if (facetsToggle && facetsPanel) {
      facetsToggle.addEventListener("click", function () {
        var open = facetsPanel.getAttribute("data-open") === "true";
        facetsPanel.setAttribute("data-open", open ? "false" : "true");
        facetsToggle.setAttribute("aria-expanded", open ? "false" : "true");
      });
    }

    function showSkeleton() {
      results.setAttribute("aria-busy", "true");
    }

    function fetchAndSwap(url, pushState, onSwapped) {
      showSkeleton();
      fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var next = doc.querySelector("[data-facets-results]");
          var nextFacets = doc.querySelector("[data-facets-panel]");
          var nextActive = doc.querySelector("[data-active-filters]");
          if (next) {
            results.innerHTML = next.innerHTML;
            results.removeAttribute("aria-busy");
          }
          if (nextFacets && facetsPanel) {
            facetsPanel.innerHTML = nextFacets.innerHTML;
          }
          var active = document.querySelector("[data-active-filters]");
          if (nextActive && active) {
            active.innerHTML = nextActive.innerHTML;
          }
          if (pushState) window.history.pushState({}, "", url);
          bindResultLinks();
          if (typeof onSwapped === "function") onSwapped(doc);
        })
        .catch(function () {
          window.location.href = url;
        });
    }

    function bindResultLinks() {
      document.querySelectorAll("[data-facet-link]").forEach(function (link) {
        link.addEventListener("click", function (e) {
          e.preventDefault();
          fetchAndSwap(link.href, true);
        });
      });
      var sortField = document.querySelector("[data-sort-select]");
      if (sortField && sortField.form) {
        sortField.addEventListener("change", function () {
          var formData = new URLSearchParams(new FormData(sortField.form));
          var url = sortField.form.getAttribute("action") + "?" + formData.toString();
          fetchAndSwap(url, true);
        });
      }
      var pageLinks = document.querySelectorAll("[data-page-link]");
      pageLinks.forEach(function (link) {
        link.addEventListener("click", function (e) {
          e.preventDefault();
          fetchAndSwap(link.href, true);
          window.scrollTo({ top: results.offsetTop - 96, behavior: "auto" });
        });
      });
    }

    bindResultLinks();

    window.addEventListener("popstate", function () {
      fetchAndSwap(window.location.href, false);
    });
  }

  /* ---------- Product card photo swap on keyboard focus (mirrors CSS hover) ---------- */

  function initCardMedia(root) {
    (root || document).querySelectorAll(".product-card__media").forEach(function (media) {
      var altImg = media.querySelector("img.is-alt");
      if (!altImg) return;
      media.closest("a").addEventListener("focus", function () {
        altImg.style.opacity = "1";
      });
      media.closest("a").addEventListener("blur", function () {
        altImg.style.opacity = "0";
      });
    });
  }

  /* ---------- Rude-tier card mask ----------
     MASK-RESEARCH-2026-09-20.md section 2(a)+(b)+(c)+(d), plus
     the 2026-09-20 follow-up ("once a mug has been hovered over then it
     stays shown... impossible to find again if all blurred"). The cover
     and its LIVE lift on hover/focus-within are still pure CSS
     (theme.css) -- what this handles is everything CSS cannot: touch (no
     hover to lift on, so the first tap has to uncover instead of
     navigating), the shared "is this card actually covered right now"
     check both the tap handler and initCardSpinHover's touchstart guard
     need, and now the STICKY part -- once a card has been revealed by any
     gesture, it stays revealed for the rest of the session, everywhere
     that product's card appears, remembered in sessionStorage (per tab,
     never written back to the record -- this is a display preference,
     not a fact). */
  function cardIsMasked(card) {
    if (card.getAttribute("data-tier") !== "rude") return false;
    if (toneSwitchOff()) return true;
    if (card.classList.contains("is-shown")) return false;
    if (document.documentElement.getAttribute("data-show-rude") === "true") return false;
    if (document.body.getAttribute("data-rude-room") === "true") return false;
    return true;
  }

  /* The card carries no product id/handle of its own (product-card.liquid
     renders none) -- its href is the one stable thing every card, SSR or
     client-built, already carries (product.url there, fields.url in
     buildProductCard), so the handle is read back off that rather than
     adding a new attribute just for this. */
  function cardHandleFromCard(card) {
    var href = card.getAttribute("href") || "";
    var m = /\/products\/([^/?#]+)/.exec(href);
    return m ? m[1] : "";
  }

  var REVEALED_CARDS_KEY = "ostrich:revealed-cards";
  var REVEALED_CARDS_MAX = 500; /* oldest dropped once the list grows past this */
  var revealedCardsCache = null;
  function getRevealedCards() {
    if (revealedCardsCache) return revealedCardsCache;
    revealedCardsCache = [];
    try {
      var parsed = JSON.parse(sessionStorage.getItem(REVEALED_CARDS_KEY) || "[]");
      if (Array.isArray(parsed)) {
        revealedCardsCache = parsed.filter(function (h) { return typeof h === "string" && h; });
      }
    } catch (e) {
      revealedCardsCache = [];
    }
    return revealedCardsCache;
  }
  function isCardHandleRevealed(handle) {
    return !!handle && getRevealedCards().indexOf(handle) !== -1;
  }
  /* try/catch on the write too -- sessionStorage can throw on a full quota
     or a private-mode browser that allows reads but refuses writes; either
     way the reveal itself already happened (the class is added regardless,
     by the caller), only "stays revealed after a reload" is lost. */
  function rememberRevealedHandle(handle) {
    if (!handle) return;
    var list = getRevealedCards();
    if (list.indexOf(handle) !== -1) return;
    list.push(handle);
    while (list.length > REVEALED_CARDS_MAX) list.shift();
    try {
      sessionStorage.setItem(REVEALED_CARDS_KEY, JSON.stringify(list));
    } catch (e) {}
  }

  /* Applies a past reveal before any gesture happens on this load -- as
     early as the per-card init can manage (the cards are server-rendered
     HTML by the time this deferred script runs at all, so a truly
     pre-paint mark is not reachable here the way layout/theme.liquid's
     inline <html data-show-rude> script is; a brief cover-then-reveal on
     a REVEALED card is acceptable, same as that script's own comment --
     the one thing that must never happen, an uncovered card that then
     gets covered, cannot happen here because nothing above ever removes
     is-shown once added). Runs for every device, not gated on hover:
     the point is "stays shown", regardless of what re-reveals it. */
  function markRevealedCards(root) {
    (root || document).querySelectorAll('.product-card[data-tier="rude"]').forEach(function (card) {
      if (card.classList.contains("is-shown")) return;
      if (isCardHandleRevealed(cardHandleFromCard(card))) card.classList.add("is-shown");
    });
  }

  /* root scopes the query the same way initCardMedia/initCardSpinHover do,
     so a card built after DOMContentLoaded (search picks, recommendations)
     gets the same handling as an SSR one. */
  function initCardMask(root) {
    var scope = root || document;
    markRevealedCards(scope);
    var hoverCapable = !!(window.matchMedia && window.matchMedia("(hover: hover)").matches);
    scope.querySelectorAll('.product-card[data-tier="rude"]').forEach(function (card) {
      if (card.hasAttribute("data-mask-bound")) return;
      card.setAttribute("data-mask-bound", "true");

      function reveal() {
        if (card.classList.contains("is-shown")) return;
        card.classList.add("is-shown");
        rememberRevealedHandle(cardHandleFromCard(card));
      }

      /* Hover must still be what triggers the FIRST reveal on desktop --
         the CSS hover lift (theme.css) already makes that feel instant;
         this only remembers it happened so it STICKS once the pointer
         leaves. pointerType guard: pointerenter also fires on a touch tap
         (per the Pointer Events spec, touch has no hover state but still
         dispatches enter/leave on contact), and must not pre-empt the
         44px two-tap gesture below -- a touch reveal is recorded by that
         path instead, on the actual tap. Keyboard focus (focusin, which
         bubbles unlike focus) reveals and stays revealed on every device,
         same as the CSS :focus-visible lift already does live -- this is
         what makes that lift permanent instead of reverting on blur. */
      card.addEventListener("pointerenter", function (ev) {
        if (ev.pointerType === "touch") return;
        reveal();
      });
      card.addEventListener("focusin", reveal);

      /* On a device that reports hover, the cover lifts and returns on
         :hover/:focus-within in CSS alone, and Cmd/Ctrl/middle-click must
         never be intercepted, so no click listener is attached there --
         the pointerenter/focusin pair above already covers how a hover
         device reveals and remembers a card. */
      if (hoverCapable) return;

      card.addEventListener("click", function (ev) {
        if (!cardIsMasked(card)) return; /* already shown, switch on, or a rude room: follow the link */
        ev.preventDefault(); /* first tap uncovers; second tap (is-shown now true) follows the link */
        reveal();
      });
    });
  }

  /* ---------- Floating "Show rude ones" switch (section 2(c), item 3) ----------
     Alan, 2026-09-20: "on mobile could the show rude option float in the
     main screen". sections/header.liquid renders a third [data-rude-switch]
     button, hidden by default, that assets/theme.js's initRudeSwitch()
     already moves in lockstep with the header/mobile-nav copies (same
     attribute, one shared listener set) -- the only new job here is
     deciding WHETHER this page should show it at all: only one with an
     actual covered card on it, and only outside a rude room (the same
     data-rude-room="true" body flag the CSS mask-off rule reads).
     assets/theme.css confines it to under 64rem and hides it again the
     instant the cart drawer opens or the page is a rude room -- this only
     ever widens visibility by removing `hidden`, CSS narrows it from
     there, so a stale "true" from before a swap never shows it somewhere
     it should not be. */
  function pageHasCoveredCard() {
    if (document.body.getAttribute("data-rude-room") === "true") return false;
    return !!document.querySelector('.product-card[data-tier="rude"]');
  }
  function updateFloatingRudeSwitchVisibility() {
    var wrap = document.querySelector("[data-rude-switch-floating]");
    if (!wrap) return;
    wrap.hidden = !pageHasCoveredCard();
  }

  /* ---------- Product card spin-GIF hover rollover ----------
     Alan, 2026-09-18 (/collections/definitions): "a real mix of heros and
     roll over images... maybe the spin as the rollover if that doesn't
     cripple loading times." It does not, because the GIF is never in the
     page's initial HTML: `data-spin` (snippets/product-card.liquid,
     buildProductCard above) carries only the url, and nothing here turns
     that into a network request until the visitor actually interacts with
     THIS card -- one request per card, ever (armed once, then the browser's
     own cache serves every later hover).

     prefers-reduced-motion skips the whole feature: no listener is even
     attached, so a reduced-motion visitor never triggers the fetch either
     -- not just "no swap" but "no bytes spent for a swap that would not
     show". The still image already on the card (the hero, or the is-alt
     fallback where no spin exists) is left exactly alone until the GIF's
     own `onload` fires, so a slow network never shows a half-loaded frame
     or a layout jump -- it just keeps showing what was already there.

     `root` scopes the query the same way `initCardMedia` does: the
     DOMContentLoaded pass covers every server-rendered card, and each
     place that builds cards with JS (renderRecommendationSection, the AI
     search results panel) calls this again scoped to just the container it
     built, so a card already armed is never bound twice. */
  function initCardSpinHover(root) {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    (root || document).querySelectorAll(".product-card[data-spin]").forEach(function (card) {
      var media = card.querySelector(".product-card__media");
      var url = card.getAttribute("data-spin");
      if (!media || !url) return;
      var loading = false;
      var ready = false;
      var hovering = false;

      function arm() {
        if (loading || ready) return;
        loading = true;
        var preload = new Image();
        preload.onload = function () {
          var spinImg = document.createElement("img");
          spinImg.className = "is-spin";
          spinImg.alt = "";
          spinImg.setAttribute("aria-hidden", "true");
          spinImg.src = url;
          media.appendChild(spinImg);
          ready = true;
          // Load can finish after the visitor already moved on (slow
          // network); only reveal it if still hovered/focused right now --
          // otherwise the still stays exactly where it was, undisturbed.
          if (hovering) media.classList.add("is-spin-active");
        };
        preload.src = url;
      }
      function show() {
        hovering = true;
        arm();
        if (ready) media.classList.add("is-spin-active");
      }
      function hide() {
        hovering = false;
        media.classList.remove("is-spin-active");
      }

      card.addEventListener("mouseenter", show);
      card.addEventListener("mouseleave", hide);
      card.addEventListener("focus", show);
      card.addEventListener("blur", hide);
      /* A covered card that has not been tapped open yet must not pay for
         a spin nobody has asked to see (MASK-RESEARCH-2026-09-20
         section 2(a)): a scroll past a masked card would otherwise arm
         the GIF download before the visitor chose to uncover it. */
      card.addEventListener("touchstart", function () {
        if (cardIsMasked(card)) return;
        hovering = true; arm();
      }, { passive: true });
    });
  }

  /* ---------- Recommendations: taxonomy-driven fetch ----------
     The section renders an empty container synchronously (so there is
     no layout shift and no-JS visitors simply see nothing extra beyond
     Shopify's own SSR recommendations - the tier rule that matters,
     same-or-cleaner recommendations only, is enforced server-side too,
     not only in this script). The Dial-era cross-lane mode (deliberately
     showing a product ABOVE the viewer's tier, behind a collapsed toggle)
     is gone along with the Dial - the theme now shows every tier, so the
     one remaining mode is same-lane.

     Primary path: query the Storefront API directly (Shopify's own
     recommendation engine knows nothing about occasion/recipient/theme -
     verified live, an 18th-birthday mug recommended "Computer Whisperer").
     Priority order, first match wins, deduped, capped at 8, current
     product excluded by handle:
       1. same primary_occasion AND overlapping recipient
       2. same primary_occasion
       3. overlapping recipient AND overlapping theme
       4. overlapping theme
     Falls back to Shopify's recommendations endpoint (data-url) only if
     the Storefront call itself fails - a successful call that finds
     nothing renders nothing (no empty collection sections), it does not
     fall back to an off-taxonomy set. */

  function getStorefrontConfig() {
    var shop = document.body.getAttribute("data-storefront-shop");
    var token = document.body.getAttribute("data-storefront-token");
    if (!shop || !token) return null;
    return {
      shop: shop,
      token: token,
      apiVersion: document.body.getAttribute("data-storefront-api-version") || "2026-07",
    };
  }

  var TIER_ORDER = ["clean", "banter", "rude"];

  function tierMetafieldFilters(tiers) {
    return tiers.map(function (t) {
      return { productMetafield: { namespace: "ostrich", key: "tier", value: t } };
    });
  }

  function tierFiltersUpTo(tier) {
    var idx = TIER_ORDER.indexOf(tier);
    if (idx < 0) idx = TIER_ORDER.length - 1;
    return tierMetafieldFilters(TIER_ORDER.slice(0, idx + 1));
  }

  function metafieldFiltersFor(key, values) {
    return (values || []).map(function (v) {
      return { productMetafield: { namespace: "ostrich", key: key, value: v } };
    });
  }

  /* Builds the priority-ordered filter sets described above. Each entry
     is a full productFilters array for one Storefront `search()` call;
     same-key filters OR together, different keys AND together (verified
     live against this API version - see ostrich-search/src/storefront.ts,
     which this mirrors client-side).

     "Same occasion" prefers ostrich.primary_occasion (single value, the
     occasion the product is genuinely FOR) and falls back to the
     ostrich.occasion list when the product has no primary. primary_occasion
     was registered as a Search & Discovery filter on 2026-08-17; before
     that it was silently ignored as a productMetafield filter, which is
     why the first version of this used the list. Shopify ignores
     unregistered metafield filters without error, so if recommendations
     ever go generic again, check the filter is still registered before
     touching this code. */
  function buildPriorityQueries(productData, tierFilterList) {
    var queries = [];
    var occasionValues = productData.primaryOccasion
      ? [productData.primaryOccasion]
      : productData.occasion;
    var occasionKey = productData.primaryOccasion ? "primary_occasion" : "occasion";
    var hasOccasion = occasionValues.length > 0;
    var hasRecipient = productData.recipient.length > 0;
    var hasTheme = productData.theme.length > 0;
    var occasionFilter = metafieldFiltersFor(occasionKey, occasionValues);
    var recipientFilters = metafieldFiltersFor("recipient", productData.recipient);
    var themeFilters = metafieldFiltersFor("theme", productData.theme);

    if (hasOccasion && hasRecipient) {
      queries.push(occasionFilter.concat(recipientFilters, tierFilterList));
    }
    if (hasOccasion) {
      queries.push(occasionFilter.concat(tierFilterList));
    }
    if (hasRecipient && hasTheme) {
      queries.push(recipientFilters.concat(themeFilters, tierFilterList));
    }
    if (hasTheme) {
      queries.push(themeFilters.concat(tierFilterList));
    }
    return queries;
  }

  var RECS_METAFIELD_IDENTIFIERS =
    '{namespace: "ostrich", key: "tier"}, {namespace: "ostrich", key: "caption"}, ' +
    '{namespace: "ostrich", key: "spoken_line"}, ' +
    '{namespace: "ostrich", key: "occasion"}, {namespace: "ostrich", key: "recipient"}, ' +
    '{namespace: "ostrich", key: "theme"}, {namespace: "ostrich", key: "personalisable"}, ' +
    // Card parity (audit item 5): without these, buildProductCard() still
    // gets a personalisable value (below) so it draws SOME delivery date
    // for a recommendation card, just the generic made-to-order fallback
    // rather than this product's own recorded dispatch_promise/
    // stock_on_hand - same two fields ostrich-search's Storefront query
    // fetches for search results, for the same reason.
    '{namespace: "ostrich", key: "dispatch_promise"}, {namespace: "ostrich", key: "stock_on_hand"}';

  var RECS_QUERY =
    "query OstrichRecs($filters: [ProductFilter!], $first: Int!) { " +
    "search(query: \"*\", types: [PRODUCT], first: $first, productFilters: $filters) { " +
    "edges { node { ... on Product { id handle title onlineStoreUrl " +
    "createdAt featuredImage { url } images(first: 3) { edges { node { url } } } " +
    "priceRange { minVariantPrice { amount currencyCode } } " +
    "metafields(identifiers: [" +
    RECS_METAFIELD_IDENTIFIERS +
    "]) { key value } } } } } }";

  function runStorefrontSearch(cfg, filters, first) {
    return fetch("https://" + cfg.shop + "/api/" + cfg.apiVersion + "/graphql.json", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "X-Shopify-Storefront-Access-Token": cfg.token,
      },
      body: JSON.stringify({ query: RECS_QUERY, variables: { filters: filters, first: first } }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error("storefront_bad_status");
        return res.json();
      })
      .then(function (data) {
        if (data.errors && data.errors.length) throw new Error("storefront_graphql_error");
        var edges = (data.data && data.data.search && data.data.search.edges) || [];
        return edges.map(function (edge) { return edge.node; });
      });
  }


  /* The spin slot is the ONE product image whose address is an animated
     container: .gif until 2026-09-19, .webp from then on (the spin lane
     uploads animated WebP now; Shopify keeps it animated and serves it a
     quarter the size). Product photographs are JPEG, so the extension is
     the signal. Same rule as snippets/product-card.liquid and
     sections/product-main.liquid, which cannot share this function. */
  function isSpinUrl(url) {
    return /\.(gif|webp)(\?|$)/i.test(url || "");
  }

  /* The path (and query) of Shopify's onlineStoreUrl, so the link resolves
     on whichever of the store's domains this page is on; the handle's path
     when Shopify gave none. */
  function sitePath(onlineStoreUrl, handle) {
    if (onlineStoreUrl) {
      try {
        var u = new URL(onlineStoreUrl);
        return u.pathname + u.search;
      } catch (e) { /* not a URL: use the handle */ }
    }
    return "/products/" + handle;
  }

  function normalizeStorefrontNode(node) {
    var byKey = {};
    (node.metafields || []).forEach(function (mf) {
      if (mf) byKey[mf.key] = mf.value;
    });
    var tier = byKey.tier === "banter" || byKey.tier === "rude" ? byKey.tier : "clean";
    var caption = byKey.caption ? byKey.caption.replace(/\s+/g, " ").trim() : "";
    /* display_line() precedence (common/design_facets.py): spoken line if
       the design has one, else caption -- so this JS-built card agrees
       with the Liquid-rendered one (product-title.liquid) and with the
       AI-search Worker's own displayTitle. */
    var spokenLine = byKey.spoken_line ? byKey.spoken_line.replace(/\s+/g, " ").trim() : "";
    var images = (node.images && node.images.edges) || [];
    var titleFallback = (node.title || "").split(" | ")[0].trim().replace(/ Mug$/, "");
    var priceInfo = node.priceRange ? node.priceRange.minVariantPrice : null;
    var imageUrl = node.featuredImage ? node.featuredImage.url : images[0] ? images[0].node.url : null;
    /* Skip an animated GIF uploaded as an Image media object (see
       snippets/product-card.liquid's matching alt_media filter) - the
       Shopify CDN does not shrink an animated GIF on resize, so picking
       one here ships it at full original size to every viewer of this
       card. images(first: 3) gives a spare candidate in case index 1 is
       one. */
    var imageUrl2 = null;
    for (var i = 0; i < images.length; i++) {
      var candidateUrl = images[i].node.url;
      if (candidateUrl === imageUrl || isSpinUrl(candidateUrl)) continue;
      imageUrl2 = candidateUrl;
      break;
    }
    /* The spin GIF, if `images(first: 3)` happened to include it -- same
       ".gif in the url" signal as above and as snippets/product-card.liquid
       (Liquid and this Storefront query both lack a mimeType field; see
       add_spin_gif.is_gif for the API-level signal this theme cannot read).
       A product whose GIF sits past index 2 has no spin on a JS-built card;
       that is this query's own existing limit (imageUrl2's comment already
       accepts it for the still-photo case), not a new gap. */
    var spinUrl = null;
    for (var k = 0; k < images.length; k++) {
      if (isSpinUrl(images[k].node.url)) { spinUrl = images[k].node.url; break; }
    }
    return {
      handle: node.handle,
      /* Site-relative, never Shopify's absolute onlineStoreUrl: that names
         the primary domain (.com) on every domain, so on .co.uk a card would
         send the buyer off-domain to a fresh cart. Same rule as
         ostrich-search/src/ranking.ts sitePath(). */
      url: sitePath(node.onlineStoreUrl, node.handle),
      tier: tier,
      imageUrl: imageUrl,
      imageUrl2: imageUrl2,
      spinUrl: spinUrl,
      title: spokenLine || caption || titleFallback,
      price: priceInfo ? priceInfo.amount : null,
      currencyCode: priceInfo ? priceInfo.currencyCode : null,
      personalisable: byKey.personalisable === "true",
      createdAt: node.createdAt || null,
      dispatchPromise: byKey.dispatch_promise || null,
      stockOnHand: Number(byKey.stock_on_hand) || 0,
    };
  }

  function readProductTaxonomy(el) {
    var primary = (el.getAttribute("data-product-primary-occasion") || "").trim();
    return {
      handle: el.getAttribute("data-product-handle") || "",
      tier: el.getAttribute("data-product-tier") || "clean",
      primaryOccasion: primary,
      occasion: safeJsonParse(el.getAttribute("data-product-occasion")) || [],
      recipient: safeJsonParse(el.getAttribute("data-product-recipient")) || [],
      theme: safeJsonParse(el.getAttribute("data-product-theme")) || [],
    };
  }

  /* Runs each priority query in turn, stopping once 8 unique (deduped,
     current-product-excluded) candidates have been collected - only
     falling through to a lower-priority query when the higher one hasn't
     filled the cap. */
  function runPriorityQueries(cfg, priorityQueries, excludeHandle) {
    var seen = {};
    var out = [];
    var i = 0;
    function next() {
      if (out.length >= 8 || i >= priorityQueries.length) return Promise.resolve(out);
      var filters = priorityQueries[i++];
      return runStorefrontSearch(cfg, filters, 12).then(function (nodes) {
        nodes.forEach(function (node) {
          if (out.length >= 8) return;
          if (node.handle === excludeHandle) return;
          if (seen[node.id]) return;
          seen[node.id] = true;
          out.push(node);
        });
        return next();
      });
    }
    return next();
  }

  function buildAndRunTaxonomyRecs(cfg, productData) {
    var tierFilterList = tierFiltersUpTo(productData.tier);
    var priorityQueries = buildPriorityQueries(productData, tierFilterList);
    if (priorityQueries.length === 0) return Promise.resolve([]);
    return runPriorityQueries(cfg, priorityQueries, productData.handle);
  }

  function renderRecommendationSection(container, mode, rawNodes) {
    if (!rawNodes || !rawNodes.length) return; /* leave SSR fallback content standing */
    var products = rawNodes.map(normalizeStorefrontNode);

    if (mode === "same-lane") {
      var head = document.createElement("div");
      head.className = "section-head";
      var h2 = document.createElement("h2");
      h2.textContent = container.getAttribute("data-heading") || "";
      head.appendChild(h2);
      var ul = document.createElement("ul");
      ul.className = "product-grid";
      products.forEach(function (p) { ul.appendChild(buildProductCard(p)); });
      container.innerHTML = "";
      container.appendChild(head);
      container.appendChild(ul);
      initCardMedia(ul);
      initCardSpinHover(ul);
      initCardMask(ul);
      updateFloatingRudeSwitchVisibility(); /* recommendations can be the only rude card on the page */
      initDeliveryDates(); /* re-tick now these cards' [data-delivery-date] elements exist */
      return;
    }
  }

  function fetchShopifyRecommendationsFallback(container) {
    var url = container.getAttribute("data-url");
    if (!url) return;
    fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var next = doc.getElementById(container.id);
        if (next && next.innerHTML.trim() !== "") {
          container.innerHTML = next.innerHTML;
        }
      })
      .catch(function () {
        /* leave the container empty; nothing to gate, nothing shown */
      });
  }

  function initRecommendations() {
    document.querySelectorAll("[data-recommendations]").forEach(function (container) {
      var mode = container.getAttribute("data-recs-mode");
      var cfg = getStorefrontConfig();
      var productMain = document.querySelector("[data-product-main]");
      if (!mode || !cfg || !productMain) {
        fetchShopifyRecommendationsFallback(container);
        return;
      }
      var productData = readProductTaxonomy(productMain);
      buildAndRunTaxonomyRecs(cfg, productData)
        .then(function (nodes) {
          renderRecommendationSection(container, mode, nodes);
        })
        .catch(function () {
          fetchShopifyRecommendationsFallback(container);
        });
    });
  }

  /* ---------- Conversational AI search: the /search page's own enhancement ----------
     REWRITTEN 2026-09-19 (Alan: "Yes, a real page"). This used to make the
     header search box itself run the Worker and drop a full product grid
     into a dropdown OVERLAID on top of whatever page the visitor was
     already on, for both Enter and while-typing. That is exactly what the
     audit was written against: searching "mum" left the URL and
     document.title on the underlying page, the back button did nothing,
     results took ~9s behind a full skeleton that could overlap the
     footer, and the Worker's own "TIER: RUDE"-shaped chip leaked.

     The header form (snippets/search-form.liquid) is a plain GET to
     routes.search_url now - see that snippet's own comment for why a
     while-typing suggestions dropdown was measured against the Worker's
     real latency and left out rather than guessed at. The Worker still
     has exactly one job here: initSearchResultsEnhance(), below, calls it
     ONCE per /search page load (never per keystroke) to re-rank the
     results Shopify's own search already rendered server-side - never a
     second, blank-first grid; the SSR grid stands until/unless a genuine
     ranked answer replaces it, so a slow or failed Worker call degrades
     to exactly what a no-JS visitor already sees. */

  /* ---------- Cloudflare Turnstile: invisible token for the AI search POST ----------
     One hidden widget, rendered lazily on first use in execution:'execute'
     mode, so there
     is never a visible challenge in the header. getTurnstileToken()
     resolves a fresh token (or null) before every AI search request -
     Turnstile tokens are single-use and expire (~300s), so calling
     turnstile.execute() again per search is required, not optional.
     Never blocks or breaks search: if the script didn't load (network
     block, extension), or a token doesn't arrive within
     TURNSTILE_TOKEN_TIMEOUT_MS, this resolves null and
     initSearchResultsEnhance() sends the request without a token - the
     Worker (src/turnstile.ts) is what decides what happens next, never
     this code. */

  var TURNSTILE_TOKEN_TIMEOUT_MS = 10000; /* a managed challenge can take several seconds on first render; the upload refuses without a token, so waiting beats failing */
  /* No `render=` parameter on purpose (2026-10-19): Cloudflare's own value
     for it is a word the UAE market's word scan refuses in code, and the
     parameter's only effect is to stop the script auto-rendering any
     `.cf-turnstile` element -- this theme has none, and renders its widgets
     itself from loadTurnstileScript() below, which waits on the script's
     load event. Default mode exposes turnstile.render/execute exactly the
     same. */
  var TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js";
  /* action -> widgetId. One widget per action (search vs. upload), same
     site key (data-turnstile-site-key), so Cloudflare's own analytics can
     tell the two apart - the site key already covers both domains this
     theme talks to. */
  var turnstileWidgets = {};
  var turnstileUnavailable = false;

  /* Injects the Turnstile script on first use instead of layout/theme.liquid
     loading it unconditionally on every page (audit FIX-LIST.md #4: 24 KB,
     88% unused, on every single page view that never touches AI search).
     Idempotent - safe to call from every place that might be the first
     interaction. getTurnstileTokenFor() already tolerates the script not
     being loaded yet (resolves no token), so a fast first search with the
     script still in flight degrades exactly as a network failure already
     did, never blocks. */
  /* Resolves true once window.turnstile exists, false if the script fails.
     A caller that asks for a token must wait on this: the first upload on a
     page used to ask for a token in the same tick it injected the script,
     got null, and the Worker refused it (2026-09-19, the photo mug's first
     live test). Loaded once per page, never re-injected. */
  var turnstileReady = null;
  function loadTurnstileScript() {
    if (turnstileReady) return turnstileReady;
    turnstileReady = new Promise(function (resolve) {
      if (typeof window.turnstile !== "undefined") { resolve(true); return; }
      var script = document.createElement("script");
      script.src = TURNSTILE_SCRIPT_URL;
      script.defer = true;
      script.onload = function () { resolve(typeof window.turnstile !== "undefined"); };
      script.onerror = function () { resolve(false); };
      document.head.appendChild(script);
    });
    return turnstileReady;
  }

  /* A fresh token for `action`, or null. Never blocks or breaks the caller:
     if the script didn't load, or a token doesn't arrive within
     TURNSTILE_TOKEN_TIMEOUT_MS, this resolves null and the caller decides
     what a missing token means for it - the AI search worker tolerates one
     (degrades gracefully); the upload worker does not (403s it), which is
     the point: Turnstile enforcement lives server-side, never here. */
  function getTurnstileTokenFor(action) {
    return loadTurnstileScript().then(function (loaded) {
      if (!loaded) return null;
      return getTurnstileTokenNow(action);
    });
  }

  function getTurnstileTokenNow(action) {
    return new Promise(function (resolve) {
      if (turnstileUnavailable || typeof window.turnstile === "undefined") {
        resolve(null);
        return;
      }

      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        resolve(null);
      }, TURNSTILE_TOKEN_TIMEOUT_MS);

      function done(token) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(token || null);
      }

      try {
        var widgetId = turnstileWidgets[action];
        if (widgetId === undefined) {
          var siteKey = document.body.getAttribute("data-turnstile-site-key");
          if (!siteKey) {
            turnstileUnavailable = true;
            done(null);
            return;
          }
          /* Off-screen, never display:none: a hidden container gives Turnstile
             no box to run its challenge in and the callback never fires
             (measured 2026-09-19: a visible container returned a token in
             under 8 s, the display:none one never did). */
          var container = document.createElement("div");
          container.setAttribute("aria-hidden", "true");
          container.style.cssText = "position:absolute;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;";
          document.body.appendChild(container);
          widgetId = window.turnstile.render(container, {
            sitekey: siteKey,
            action: action,
            execution: "execute",
            "response-field": false,
            callback: done,
            "error-callback": function () { done(null); },
            "timeout-callback": function () { done(null); },
          });
          if (typeof widgetId === "undefined") {
            turnstileUnavailable = true;
            done(null);
            return;
          }
          turnstileWidgets[action] = widgetId;
        } else {
          window.turnstile.reset(widgetId);
        }
        window.turnstile.execute(widgetId);
      } catch (e) {
        turnstileUnavailable = true;
        done(null);
      }
    });
  }

  /* Back-compat name: every existing AI-search caller keeps working
     unchanged. */
  function getTurnstileToken() {
    return getTurnstileTokenFor("ostrich-ai-search");
  }

  /* Builds the plain-word interpretation sentence ("Showing mugs for Mum
     and Gardening") from the Worker's own chips array, never the raw
     "TIER: RUDE" / "for: Mum" key:value shapes the audit found (item:
     "interpretation shown as plain words... never as key: value chips").
     Same "TIER:" strip the old renderer already did, plus "for: X" -> "for
     X" so the whole thing reads as one sentence, not a label followed by a
     value. Returns "" when there's nothing worth saying (no facets
     mapped, e.g. a bare keyword query) - the caller hides the line. */
  function buildInterpretationSentence(chips) {
    var words = (Array.isArray(chips) ? chips : [])
      .filter(function (c) { return typeof c === "string" && !/^[ \t]*TIER[ \t]*:/i.test(c); })
      .map(function (c) {
        var m = /^[ \t]*for:[ \t]*(.+)$/i.exec(c);
        return m ? m[1] : c; /* the template already says "for" */
      });
    if (!words.length) return "";
    var joined = words.length === 1
      ? words[0]
      : words.slice(0, -1).join(", ") + " and " + words[words.length - 1];
    var template = getOstrichStrings().showingFor || "Showing mugs for __LIST__.";
    return template.replace("__LIST__", joined);
  }

  /* Same empty state snippets/search-results.liquid renders server-side
     (same mascot, same "Colin has looked" copy, same markup) for the
     genuinely-nothing-found case reached only through a live Worker
     answer - never shown in place of a slow/failed Worker call, which
     leaves the SSR grid exactly as it was instead (see
     initSearchResultsEnhance's .catch). */
  function buildSearchEmptyState(query) {
    var strings = getOstrichStrings();
    var wrap = document.createElement("div");
    wrap.className = "empty-state";
    if (strings.noResultsMascotUrl) {
      var mascotImg = document.createElement("img");
      mascotImg.src = strings.noResultsMascotUrl;
      if (strings.noResultsMascotUrl2x) {
        mascotImg.srcset = strings.noResultsMascotUrl + " 600w, " + strings.noResultsMascotUrl2x + " 1200w";
        mascotImg.sizes = "120px";
      }
      mascotImg.alt = strings.mascotAlt || "Colin, the Ostrich Designs ostrich";
      mascotImg.className = "state-mascot state-mascot--small";
      mascotImg.width = 120;
      mascotImg.height = 120;
      mascotImg.loading = "lazy";
      mascotImg.addEventListener("error", function () { mascotImg.remove(); });
      wrap.appendChild(mascotImg);
    }
    var p = document.createElement("p");
    p.className = "h3";
    var template = strings.noResults || "Nothing for “__TERMS__”. Colin has looked. Try a person, an occasion, or a swear word.";
    p.textContent = template.replace("__TERMS__", query);
    wrap.appendChild(p);
    return wrap;
  }

  /* general.search.results_count ("N mugs") re-derived for the count
     Shopify's own template already rendered - resultsCountOne is always
     literally "1 mug" (no substitution needed, English has one singular
     form); resultsCountOther carries the __COUNT__ placeholder the same
     way every other templated string here does. */
  function formatMugCount(n) {
    var strings = getOstrichStrings();
    if (n === 1) return strings.resultsCountOne || "1 mug";
    var template = strings.resultsCountOther || "__COUNT__ mugs";
    return template.replace("__COUNT__", String(n));
  }

  /* THE /search PAGE ITSELF - runs once per page load, never per
     keystroke (see the file-header comment above). Calls the Worker with
     the query Shopify's search template already resolved server-side
     (data-initial-query), and on a genuine ranked answer REPLACES the
     SSR grid with one built from that answer through the exact same
     buildProductCard()/product-card-template every other client-rendered
     card already uses - same delivery date, same "New"/"Add a name"
     badge as an SSR card (ostrich-search's dispatchPromise/stockOnHand/
     personalisable/createdAt fields exist specifically so this is true;
     see buildProductCard's own delivery-date handling above).

     Degrades to "leave the SSR grid exactly as it is" - never a second,
     blank-first grid, never a stuck note - on every failure mode: no JS
     ran this far (couldn't happen, but), Worker unreachable, non-200,
     timeout, or the Worker's own `degraded: true` (a naive keyword guess
     is no better than the plain search already on the page). */
  /* Back navigation restores whatever was typed into the header search
     field (bfcache and form restoration ignore autocomplete="off"), so a
     visitor who searched "mum", went back and typed "swear" searched
     "mumswear" (read back 2026-09-19). On pageshow the field returns to
     the value the server rendered: the query on a search page, empty
     elsewhere. */
  function initSearchFieldReset() {
    window.addEventListener("pageshow", function () {
      document.querySelectorAll('form[action$="/search"] input[name="q"]').forEach(function (input) {
        input.value = input.getAttribute("value") || "";
      });
    });
  }

  function initSearchResultsEnhance() {
    /* SIDELINED 2026-09-25: the Worker is only called when the theme
       setting ai_search_enabled is on (layout/theme.liquid stamps it on
       <body>); sections/search-results.liquid also withholds
       data-initial-query when it is off, so this returns twice over. */
    if (document.body.getAttribute("data-ai-search") !== "true") return;
    var gridWrap = document.getElementById("AiSearchInline");
    if (!gridWrap) return;
    var query = (gridWrap.getAttribute("data-initial-query") || "").trim();
    if (!query) return;
    var template = document.querySelector("[data-product-card-template]");
    if (!template) return;

    var countEl = document.querySelector("[data-search-count]");
    var interpretationEl = document.querySelector("[data-ai-search-interpretation]");
    var rankingEl = document.querySelector("[data-ai-search-ranking]");
    if (rankingEl) rankingEl.hidden = false;

    loadTurnstileScript(); /* about to call the Worker on load - no focus event to wait for */

    var sanitizedQuery = query.slice(0, AI_SEARCH_MAX_QUERY_LENGTH);
    var controller = new AbortController();
    var timeoutId = null;
    function stopRanking() {
      if (timeoutId) clearTimeout(timeoutId);
      if (rankingEl) rankingEl.hidden = true;
    }

    getTurnstileToken()
      .then(function (token) {
        /* The abort clock starts when the Worker is called, not before the
           Turnstile token is requested: the token alone can take two or
           three seconds on a first render, and 2026-09-19's read-back saw
           the ranking never land because the old 4 s clock had counted
           the token against the Worker. The server-rendered grid stands
           throughout, so a slow rank costs the reader nothing but the
           "Ranking" note. */
        timeoutId = setTimeout(function () { controller.abort(); }, AI_SEARCH_TIMEOUT_MS);
        return fetch(AI_SEARCH_URL, {
          method: "POST",
          headers: { "content-type": "application/json" },
          /* The Dial is gone and the store shows every tier now;
             "rude" is the Worker's own word for "no filter"
             (parseTierPreference defaults a missing/invalid value to
             "clean", the narrowest). */
          body: JSON.stringify({ query: sanitizedQuery, tier: toneSwitchOff() ? "banter" : "rude", turnstileToken: token || undefined }),
          signal: controller.signal,
        });
      })
      .then(function (res) {
        if (!res.ok) throw new Error("ai_search_bad_status");
        return res.json();
      })
      .then(function (data) {
        stopRanking();
        if (!data || data.degraded) return; /* leave the SSR grid standing - see file-header comment */

        var results = Array.isArray(data.results) ? data.results : [];

        if (interpretationEl) {
          var sentence = buildInterpretationSentence(data.chips);
          interpretationEl.textContent = sentence;
          interpretationEl.hidden = !sentence;
        }
        /* The Worker returns at most eight ranked picks (ranking.ts limit 8),
           not the whole match set, so they cannot replace the grid or the
           count: read back 2026-09-19, "213 mugs" became "8 mugs". The picks
           sit in their own row above Shopify's full results, and the count
           stays the full count. With no picks, nothing changes. */
        if (!results.length) return;

        var picks = document.createElement("section");
        picks.className = "search-picks";
        picks.setAttribute("aria-label", getOstrichStrings().bestMatches || "Best matches");
        var picksHead = document.createElement("h2");
        picksHead.className = "h3";
        picksHead.textContent = getOstrichStrings().bestMatches || "Best matches";
        picks.appendChild(picksHead);
        var grid = document.createElement("ul");
        grid.className = "product-grid";
        results.forEach(function (r) {
          var tier = r.tier === "banter" || r.tier === "rude" ? r.tier : "clean";
          var li = document.createElement("li");
          li.appendChild(
            buildProductCard({
              handle: r.handle,
              url: r.url,
              tier: tier,
              imageUrl: r.imageUrl,
              imageUrl2: r.imageUrl2,
              spinUrl: r.spinUrl,
              title: r.displayTitle || r.caption || r.title || "",
              price: r.price,
              currencyCode: r.currencyCode,
              createdAt: r.createdAt,
              personalisable: r.personalisable,
              dispatchPromise: r.dispatchPromise,
              stockOnHand: r.stockOnHand,
            }),
          );
          grid.appendChild(li);
        });
        picks.appendChild(grid);
        var old = gridWrap.parentNode.querySelector(".search-picks");
        if (old) old.remove();
        gridWrap.parentNode.insertBefore(picks, gridWrap);
        initCardMedia(grid);
        initCardSpinHover(grid);
        initCardMask(grid);
        updateFloatingRudeSwitchVisibility(); /* the Worker's ranked picks can be the page's only rude card */
        initDeliveryDates(); /* re-tick now the new cards exist - see that function's own comment */
      })
      .catch(function () {
        /* Network failure, timeout, non-200, Turnstile refusal: the
           honest degrade is exactly what a no-JS visitor already sees -
           the SSR grid, untouched. */
        stopRanking();
      });
  }

  /* ---------- Product gallery ---------- */

  function initGallery() {
    document.querySelectorAll("[data-gallery]").forEach(function (gallery) {
      var main = gallery.querySelector("[data-gallery-main]");
      var thumbs = Array.prototype.slice.call(gallery.querySelectorAll("[data-gallery-thumb]"));
      if (!main || !thumbs.length) return;
      // The 360 spin, if this product has one. It is a sibling of the main
      // <img> rather than something swapped into its src, so selecting it
      // means showing one and hiding the other.
      var videoWrap = gallery.querySelector("[data-gallery-video]");
      var video = videoWrap ? videoWrap.querySelector("video") : null;
      // The 3D model, if this product has one. Shopify's model-viewer-ui
      // gives the <model-viewer> its controls; shopify-xr turns the
      // "View in your space" button on where the phone can do AR (it
      // removes data-shopify-xr-hidden itself). Both are Shopify's own
      // features, loaded once, only on a page that has a model.
      var modelWrap = gallery.querySelector("[data-gallery-model]");
      function loadModelFeatures() {
        if (!modelWrap || gallery.__modelFeatures || !window.Shopify || !window.Shopify.loadFeatures) return;
        gallery.__modelFeatures = true;
        var json = gallery.querySelector("[data-product-models]");
        window.Shopify.loadFeatures([
          { name: "shopify-xr", version: "1.0", onLoad: function (errors) {
              if (errors) return;
              function setup() {
                if (!window.ShopifyXR) {
                  document.addEventListener("shopify_xr_initialized", setup, { once: true });
                  return;
                }
                try { if (json) window.ShopifyXR.addModels(JSON.parse(json.textContent)); } catch (e) {}
                window.ShopifyXR.setupXRElements();
              }
              setup();
            } },
          { name: "model-viewer-ui", version: "1.0", onLoad: function (errors) {
              if (errors) return;
              modelWrap.querySelectorAll("model-viewer").forEach(function (mv) {
                if (!mv.__ui && window.Shopify.ModelViewerUI) mv.__ui = new window.Shopify.ModelViewerUI(mv);
                // ModelViewerUI wraps the <model-viewer> in its own div
                // (.shopify-model-viewer-ui) with no height of its own, so
                // the viewer's CSS height: 100% resolved to 0px and the page
                // showed the zoom buttons over nothing (Alan, 2026-09-19,
                // Safari on the phone; the same in Chrome). The wrapper is
                // this script's artefact, so it is sized here, the moment
                // it exists: it fills the aspect-ratio box and the viewer
                // fills it.
                var ui = mv.parentElement;
                if (ui && ui.classList.contains("shopify-model-viewer-ui")) ui.style.height = "100%";
              });
            } }
        ]);
      }
      loadModelFeatures();

      function showModel() {
        if (!modelWrap) return;
        if (videoWrap) { videoWrap.hidden = true; if (video) video.pause(); }
        main.hidden = true;
        modelWrap.hidden = false;
      }

      function showVideo() {
        if (!videoWrap) return;
        if (modelWrap) modelWrap.hidden = true;
        videoWrap.hidden = false;
        main.hidden = true;
        // A muted autoplay loop normally starts on its own; play() covers
        // the case where the element was hidden when the page loaded and
        // the browser declined. It can reject, and a rejected promise with
        // no handler is an unhandled rejection in the console.
        if (video) { var p = video.play(); if (p && p.catch) p.catch(function () {}); }
      }

      function showPhoto(src, alt) {
        if (modelWrap) modelWrap.hidden = true;
        if (videoWrap) {
          videoWrap.hidden = true;
          if (video) video.pause();
        }
        main.hidden = false;
        // The hero <img> carries a srcset, and a browser that honours a
        // srcset ignores a new src -- so every thumb click before
        // 2026-09-19 left the hero in place (Alan: "the 360 isnt spinning
        // on my screen"). The candidates are the hero's own sizes; drop
        // them and the one src is what shows. width=900 in data-full
        // already asks the CDN for the right size.
        if (src) {
          main.removeAttribute("srcset");
          main.removeAttribute("sizes");
          main.src = src;
        }
        if (alt) main.alt = alt;
      }

      thumbs.forEach(function (thumb) {
        thumb.addEventListener("click", function () {
          if (thumb.getAttribute("data-media-type") === "video") {
            showVideo();
          } else if (thumb.getAttribute("data-media-type") === "model") {
            showModel();
          } else {
            showPhoto(thumb.getAttribute("data-full"), thumb.getAttribute("data-alt"));
          }
          thumbs.forEach(function (t) { t.setAttribute("aria-pressed", t === thumb ? "true" : "false"); });
        });
      });

      initGallerySwipe(gallery, main, thumbs, modelWrap);
      initLightbox(gallery, main, thumbs);
    });
  }

  /* ---------- Swipe the main photo, and a full-screen viewer ----------

     Alan, 2026-09-29: "on the mobile site should the images not pop into
     light boxes when clicked? is that best practice?" It is (Baymard's
     mobile product-page research): a phone buyer expects to swipe the main
     photo, tap it to go full screen, and pinch or tap to zoom -- and on this
     shop the joke is the text on the mug, so reading it close up is the
     purchase. Before this, tapping the photo did nothing and the only way to
     the next one was a 60px thumbnail.

     A SWIPE PRESSES THE NEXT THUMB, for the reason the variant code presses
     one: initGallery owns "show this photo", and on a badge group pressing a
     thumb also chooses the variant, so a swipe does exactly what a tap on
     the thumb does and nothing else. Never on the 3D model, whose own drag
     rotates it.

     THE VIEWER ONLY LOOKS. It shows the product's photos and never moves the
     gallery or the variant. Back closes it (a history entry is pushed on
     open), as does Escape and the close button; tap an image to zoom to
     250% and pan, tap again to fit; native pinch-zoom is left alone. */

  function initGallerySwipe(gallery, main, thumbs, modelWrap) {
    var stage = main.parentElement;
    var strip = thumbs[0].parentElement;
    var sx = null, sy = null;
    function pressed() {
      for (var i = 0; i < thumbs.length; i++) if (thumbs[i].getAttribute("aria-pressed") === "true") return i;
      return 0;
    }
    stage.addEventListener("touchstart", function (e) {
      sx = null;
      if (e.touches.length !== 1 || (modelWrap && !modelWrap.hidden)) return;
      sx = e.touches[0].clientX; sy = e.touches[0].clientY;
    }, { passive: true });
    stage.addEventListener("touchend", function (e) {
      if (sx === null) return;
      var t = e.changedTouches[0], dx = t.clientX - sx, dy = t.clientY - sy;
      sx = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      var next = thumbs[pressed() + (dx < 0 ? 1 : -1)];
      if (!next) return;
      stage.__swipedAt = Date.now();
      next.click();
      // Bring the pressed thumb into the strip's view by scrolling the STRIP,
      // never scrollIntoView, which would also scroll the page to the strip.
      var sr = strip.getBoundingClientRect(), nr = next.getBoundingClientRect();
      if (nr.left < sr.left || nr.right > sr.right) strip.scrollLeft += nr.left - sr.left - (sr.width - nr.width) / 2;
    }, { passive: true });
  }

  function initLightbox(gallery, main, thumbs) {
    var photos = thumbs.filter(function (t) { return t.getAttribute("data-media-type") === "image"; });
    if (!photos.length || typeof HTMLDialogElement !== "function") return;
    var stage = main.parentElement;
    var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    var dialog = document.createElement("dialog");
    dialog.className = "lightbox";
    dialog.setAttribute("aria-label", "Product photos");
    dialog.innerHTML =
      '<div class="lightbox__bar"><span class="lightbox__count" aria-live="polite"></span>' +
      '<button type="button" class="lightbox__close" aria-label="Close">&times;</button></div>' +
      '<div class="lightbox__track"></div>' +
      '<button type="button" class="lightbox__nav lightbox__nav--prev" aria-label="Previous photo">&#8249;</button>' +
      '<button type="button" class="lightbox__nav lightbox__nav--next" aria-label="Next photo">&#8250;</button>';
    var track = dialog.querySelector(".lightbox__track");
    var count = dialog.querySelector(".lightbox__count");
    photos.forEach(function (t) {
      var fig = document.createElement("figure");
      fig.className = "lightbox__slide";
      var box = document.createElement("div");
      box.className = "lightbox__zoom";
      var img = document.createElement("img");
      img.alt = t.getAttribute("data-alt") || "";
      img.loading = "lazy";
      img.decoding = "async";
      // The thumb's data-full asks the CDN for 900px; full screen on a
      // 3x phone wants more, and the CDN resizes on the same URL.
      img.setAttribute("data-src", (t.getAttribute("data-full") || "").replace(/width=\d+/, "width=1800"));
      box.appendChild(img);
      fig.appendChild(box);
      track.appendChild(fig);
    });
    document.body.appendChild(dialog);
    var slides = Array.prototype.slice.call(track.children);

    var enlarge = document.createElement("button");
    enlarge.type = "button";
    enlarge.className = "product-gallery__enlarge";
    enlarge.setAttribute("aria-label", "View photos full screen");
    enlarge.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.5 15.5 21 21M10.5 7.5v6M7.5 10.5h6"/></svg>';
    stage.appendChild(enlarge);

    function index() { return Math.round(track.scrollLeft / Math.max(1, track.clientWidth)); }
    function load(i) {
      [i - 1, i, i + 1].forEach(function (k) {
        var img = slides[k] && slides[k].querySelector("img");
        if (img && !img.src) img.src = img.getAttribute("data-src");
      });
    }
    function update() {
      var i = index();
      count.textContent = (i + 1) + " / " + slides.length;
      load(i);
    }
    function unzoom() {
      slides.forEach(function (s) { s.classList.remove("is-zoomed"); });
      dialog.classList.remove("is-zoomed");
    }
    function go(i, smooth) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      unzoom();
      track.scrollTo({ left: i * track.clientWidth, behavior: smooth && !reduce ? "smooth" : "auto" });
      update();
    }
    function open() {
      var p = thumbs.filter(function (t) { return t.getAttribute("aria-pressed") === "true"; })[0];
      var start = Math.max(0, photos.indexOf(p));
      document.documentElement.classList.add("lightbox-open");
      dialog.showModal();
      try { history.pushState({ odLightbox: true }, ""); } catch (e) {}
      requestAnimationFrame(function () { go(start, false); });
    }
    function shut() {
      if (!dialog.open) return;
      unzoom();
      dialog.close();
      document.documentElement.classList.remove("lightbox-open");
      stage.querySelector("[data-gallery-main]") && enlarge.focus({ preventScroll: true });
    }
    // Every visible close goes through history, so the back gesture and the
    // close button leave the same history behind.
    function requestClose() {
      if (history.state && history.state.odLightbox) history.back();
      else shut();
    }
    window.addEventListener("popstate", shut);
    dialog.addEventListener("cancel", function (e) { e.preventDefault(); requestClose(); });
    dialog.querySelector(".lightbox__close").addEventListener("click", requestClose);
    dialog.querySelector(".lightbox__nav--prev").addEventListener("click", function () { go(index() - 1, true); });
    dialog.querySelector(".lightbox__nav--next").addEventListener("click", function () { go(index() + 1, true); });
    dialog.addEventListener("keydown", function (e) {
      if (e.key === "ArrowLeft") go(index() - 1, true);
      if (e.key === "ArrowRight") go(index() + 1, true);
    });
    var tick = null;
    track.addEventListener("scroll", function () {
      if (tick) return;
      tick = requestAnimationFrame(function () { tick = null; update(); });
    }, { passive: true });

    // Tap to zoom where you tapped; tap again to fit.
    track.addEventListener("click", function (e) {
      var img = e.target.closest && e.target.closest(".lightbox__zoom img");
      if (!img) return;
      var slide = img.closest(".lightbox__slide"), box = img.parentElement;
      if (slide.classList.contains("is-zoomed")) { unzoom(); return; }
      var r = img.getBoundingClientRect();
      var fx = (e.clientX - r.left) / r.width, fy = (e.clientY - r.top) / r.height;
      slide.classList.add("is-zoomed");
      dialog.classList.add("is-zoomed");
      box.scrollLeft = fx * box.scrollWidth - box.clientWidth / 2;
      box.scrollTop = fy * box.scrollHeight - box.clientHeight / 2;
    });

    enlarge.addEventListener("click", open);
    main.addEventListener("click", function () {
      if (stage.__swipedAt && Date.now() - stage.__swipedAt < 400) return;
      open();
    });
  }

  /* ---------- Quantity stepper ---------- */

  function initQuantity() {
    document.querySelectorAll("[data-qty-field]").forEach(function (field) {
      var input = field.querySelector("input");
      var minus = field.querySelector("[data-qty-minus]");
      var plus = field.querySelector("[data-qty-plus]");
      if (!input) return;
      function clamp(v) {
        var min = parseInt(input.min || "1", 10);
        return Math.max(min, v);
      }
      if (minus) minus.addEventListener("click", function () {
        input.value = clamp((parseInt(input.value, 10) || 1) - 1);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
      if (plus) plus.addEventListener("click", function () {
        input.value = clamp((parseInt(input.value, 10) || 1) + 1);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });
  }

  /* ---------- Variant picker ----------
     Keeps the hidden variant id, price and button state in sync with the
     selected options. Server-rendered radios already reflect the
     currently selected variant, so this only needs to run on change;
     without JS the form still submits the variant that was selected on
     load (the page just will not update mid-visit). */

  function initVariantPicker() {
    document.querySelectorAll("[data-product-form]").forEach(function (form) {
      var variantsScript = form.querySelector("[data-product-variants]");
      var optionInputs = Array.prototype.slice.call(form.querySelectorAll("[data-option-input]"));
      if (!variantsScript || !optionInputs.length) return;

      var variants;
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        return;
      }

      var idInput = form.querySelector("[data-product-form-id]");
      var submitBtn = form.querySelector("[data-product-form-submit]");
      var priceWrap = document.querySelector("[data-product-price]");

      /* THE HEADING FOLLOWS THE MEMBER (2026-09-19). `data-product-title`
         is server-rendered from the same variant this script starts from
         (product-main.liquid's `h1_text`), so `currentMemberText` opens
         already in sync -- this only tracks what to look for inside
         `document.title` the next time it has to change. */
      var h1El = document.querySelector("[data-product-title]");
      var currentMemberText = h1El ? h1El.textContent : null;

      function currentOptions() {
        var byPosition = {};
        optionInputs.forEach(function (input) {
          if (input.checked) byPosition[input.getAttribute("data-option-position")] = input.value;
        });
        var positions = Object.keys(byPosition).map(Number).sort(function (a, b) { return a - b; });
        return positions.map(function (p) { return byPosition[p]; });
      }

      function matchVariant() {
        var opts = currentOptions();
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          if (v.options.length === opts.length && v.options.every(function (o, idx) { return o === opts[idx]; })) {
            return v;
          }
        }
        return null;
      }

      function variantById(id) {
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === String(id)) return variants[i];
        }
        return null;
      }

      /* H1 text and the leading part of document.title, from the
         variant's own `title` (product-main.liquid: the design axis's
         value -- Mountain/Letter/Age -- or `null` where no single design
         axis exists, e.g. an ordinary blank-only product; `null` leaves
         both alone). String-replaced rather than reassigned outright so
         the trailing " · Shop Name" (layout/theme.liquid) survives. */
      function updateHeading(variant) {
        if (!h1El || variant.title == null || variant.title === currentMemberText) return;
        if (currentMemberText) {
          document.title = document.title.split(currentMemberText).join(variant.title);
        }
        h1El.textContent = variant.title;
        currentMemberText = variant.title;
        var stickyTitle = document.querySelector("[data-sticky-buy-bar-title]");
        if (stickyTitle) stickyTitle.textContent = variant.title;
      }

      /* THE URL FOLLOWS THE MEMBER. `replaceState`, never `pushState` --
         one page view, not a history entry per swatch click -- so Back
         leaves the product page entirely rather than stepping back
         through mountains. Only called for an actual user action; the
         initial `update()` at load never rewrites a URL nobody asked to
         change (the at-rest variant is already reachable by the SAME
         server-side rule on a plain reload). */
      function syncUrl(variant) {
        if (!window.history || !window.history.replaceState) return;
        var url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState(window.history.state, "", url);
      }

      /* Shared with the gallery-thumb and popstate handlers below: checks
         the radios matching one variant object, same option-position read
         `currentOptions()` uses in reverse. Returns whether anything
         actually changed, so a caller can skip a no-op `update()`. */
      function setOptionsFromVariant(v) {
        var changed = false;
        optionInputs.forEach(function (input) {
          var pos = Number(input.getAttribute("data-option-position"));
          var want = v.options[pos - 1];
          if (want === undefined) return;
          if (input.value === want) {
            if (!input.checked) { input.checked = true; changed = true; }
          } else if (input.checked) {
            input.checked = false;
          }
        });
        return changed;
      }

      function update(isUserAction) {
        var variant = matchVariant();
        if (!variant) {
          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.textContent = submitBtn.getAttribute("data-label-unavailable");
          }
          return;
        }
        if (idInput) idInput.value = variant.id;
        if (priceWrap) {
          var priceEl = priceWrap.querySelector(".price");
          if (priceEl) priceEl.textContent = variant.priceHtml;
        }
        if (submitBtn) {
          submitBtn.disabled = !variant.available;
          submitBtn.textContent = variant.available
            ? submitBtn.getAttribute("data-label-add")
            : submitBtn.getAttribute("data-label-sold-out");
        }
        /* STICKY BUY BAR (do-now item 2): the same two facts the main
           button and price already show, kept in sync here rather than a
           second variant-matching pass -- this is the one place that
           already knows which variant is current. */
        var stickyPrice = document.querySelector("[data-sticky-buy-bar-price]");
        if (stickyPrice) stickyPrice.textContent = variant.priceHtml;
        var stickySubmit = document.querySelector("[data-sticky-buy-bar-submit]");
        if (stickySubmit) {
          stickySubmit.disabled = !variant.available;
          stickySubmit.textContent = variant.available
            ? stickySubmit.getAttribute("data-label-add")
            : stickySubmit.getAttribute("data-label-sold-out");
        }
        showVariantMedia(variant);
        updateHeading(variant);
        if (isUserAction) syncUrl(variant);
      }

      /* Move the gallery to the variant's own photograph.

         BY CLICKING THE THUMB, not by writing to the main <img>. initGallery
         already owns "show this photo" -- it hides the 360 video, sets the
         src and the alt, and moves aria-pressed -- and a second copy here
         would be two writers of one rule, disagreeing the first time either
         changes. The thumb is the gallery's own control; this presses it.

         Matched on the MEDIA ID, not the alt text. Alt is already a join key
         between our record and Shopify's media and it is one that has bitten
         this repo before; inside one page the id is exact and free.

         Silent when the variant has no image of its own on a ONE-variant
         product -- the ordinary case, where the gallery is already
         showing the only thing there is. On a MULTI-variant product a
         missing `mediaId` is a data gap, not a quiet no-op: warn naming
         the variant so the next person sees the gap instead of assuming
         the picker is broken (this is a developer signal, not user
         copy -- console.warn only, nothing rendered). */
      function showVariantMedia(variant) {
        if (!variant.mediaId) {
          if (variants.length > 1) {
            console.warn("product-main: no gallery media for variant " + variant.id);
          }
          return;
        }
        var thumb = document.querySelector(
          '[data-gallery-thumb][data-media-id="' + variant.mediaId + '"]');
        if (!thumb) return;
        if (thumb.getAttribute("aria-pressed") === "true") return;
        thumb.click();
      }

      optionInputs.forEach(function (input) {
        input.addEventListener("change", function () { update(true); });
      });
      // AND ONCE ON LOAD. The page arrives with the first variant checked
      // and the product's FEATURED photo showing, which on a multi-colour
      // listing is another colour's mug (Grandad pair: Light Blue checked,
      // the yellow-handled hero showing). Alan, 2026-09-19: "swatched
      // listings rarely swap to the correct colour". The same update the
      // swatch click runs, run now, so picture and swatch agree from the
      // first frame. `false` -- not a user action, so this never rewrites
      // a URL nobody asked to change.
      update(false);

      // BACK/FORWARD RE-SYNCS FROM THE URL. `syncUrl` above only ever
      // *writes* `?variant=`; this is the read half, so a Back press after
      // a swatch change moves the radios/price/media/H1 back too rather
      // than leaving them on whatever was last clicked while the address
      // bar disagrees. `update(false)`: reading the URL back is not itself
      // a new user action on THIS variant, so it must not push another
      // history entry.
      window.addEventListener("popstate", function () {
        var params = new URLSearchParams(window.location.search);
        var wantId = params.get("variant");
        if (!wantId) return;
        var v = variantById(wantId);
        if (!v) return;
        setOptionsFromVariant(v);
        update(false);
      });

      /* AND THE OTHER DIRECTION. Alan, 2026-08-28: "while you fixed clicking
         the variant moves the gallery, clicking the gallery doesnt move the
         variant, both need to work".

         He is right, and on a grouped listing it is the half that matters
         more: the gallery is 23 badges, so clicking one IS choosing one. A
         buyer who clicks the Welsh dragon and then Add to Basket would have
         been sent the England mug -- the picture said one thing and the
         hidden variant id said another, which is worse than the gallery not
         moving at all.

         Driven from HERE rather than from initGallery, for the same reason
         the forward direction is driven from here: the gallery owns "show
         this photo" and knows nothing about variants, and teaching it would
         put variant logic in two places. */
      /* Several variants can share one photograph (a mountain's ceramic
         and enamel mugs until the enamel has its own; read back 2026-09-20:
         choosing Crib Goch flipped the mug type to Enamel because the last
         variant written for that photo won). A thumbnail names a photo, not
         a variant, so among the variants that share it the one nearest the
         current selection is chosen: the current variant itself if it has
         the photo, else the one agreeing with the current selection on the
         most option positions. A programmatic thumb click made by
         showVariantMedia never re-derives the variant at all. */
      var variantsByMedia = {};
      variants.forEach(function (v) {
        if (!v.mediaId) return;
        var key = String(v.mediaId);
        (variantsByMedia[key] = variantsByMedia[key] || []).push(v);
      });
      function nearestVariantForMedia(mediaId) {
        var pool = variantsByMedia[String(mediaId)];
        if (!pool || !pool.length) return null;
        var current = matchVariant();
        if (current && pool.indexOf(current) !== -1) return current;
        var opts = currentOptions();
        var best = pool[0], bestScore = -1;
        pool.forEach(function (v) {
          var score = 0;
          v.options.forEach(function (o, idx) { if (o === opts[idx]) score++; });
          if (score > bestScore) { best = v; bestScore = score; }
        });
        return best;
      }
      document.querySelectorAll("[data-gallery-thumb][data-media-id]")
        .forEach(function (thumb) {
          thumb.addEventListener("click", function (event) {
            if (event && event.isTrusted === false) return; // showVariantMedia's own click
            var v = nearestVariantForMedia(thumb.getAttribute("data-media-id"));
            if (!v) return;          // a scene shot, not any variant's own
            var changed = setOptionsFromVariant(v);
            // NO EVENT DISPATCH, and no loop: `update()` is called directly.
            // Dispatching `change` would re-enter through the listener above,
            // and update() clicks the matching thumb -- which is this thumb,
            // whose handler would run again. The aria-pressed guard already
            // stops that recursing, but not relying on a guard to break a
            // cycle is cheaper than reasoning about whether it always will.
            // `true` -- clicking a gallery thumbnail IS choosing a variant,
            // same as a swatch click, so the URL follows it too.
            if (changed) update(true);
          });
        });
    });
  }

  /* ---------- Hero: lifestyle photo carousel ----------
     Crossfades between the slides sections/hero.liquid renders (up to
     six), pauses on hover/focus, and is entirely inert under
     prefers-reduced-motion (CSS already keeps slide 0 statically visible
     and hides the dots in that case - this function just never runs, so
     there is no timer and no rotation to disable).
     Never assumes the photos exist: bails immediately if .hero carries
     hero--no-photo (set by the first slide's onerror in the Liquid), and
     keeps watching for that class in case the image fails after this has
     already started (a slow 404 can resolve after DOMContentLoaded). */
  function initHeroCarousel() {
    var hero = document.querySelector("[data-hero]");
    if (!hero) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (hero.classList.contains("hero--no-photo")) return;

    var photo = hero.querySelector("[data-hero-photo]");
    var dotsWrap = hero.querySelector("[data-hero-dots]");
    var status = hero.querySelector("[data-hero-status]");
    if (!photo) return;

    var index = 0;
    var timer = null;

    function slides() {
      return Array.prototype.slice.call(photo.querySelectorAll("[data-hero-slide]"));
    }
    function dots() {
      return dotsWrap ? Array.prototype.slice.call(dotsWrap.querySelectorAll("[data-hero-dot]")) : [];
    }

    function goTo(i) {
      var s = slides();
      if (!s.length) return;
      index = ((i % s.length) + s.length) % s.length;
      s.forEach(function (slide, si) {
        slide.classList.toggle("is-active", si === index);
      });
      dots().forEach(function (dot, di) {
        var on = di === index;
        dot.classList.toggle("is-active", on);
        dot.setAttribute("aria-current", on ? "true" : "false");
      });
      if (status) {
        var template = status.getAttribute("data-hero-status-template") || "";
        status.textContent = template
          .replace("__CURRENT__", index + 1)
          .replace("__TOTAL__", s.length);
      }
    }

    function next() {
      if (slides().length > 1) goTo(index + 1);
    }

    function start() {
      stop();
      if (slides().length > 1) timer = setInterval(next, 6000);
    }
    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    dots().forEach(function (dot) {
      dot.addEventListener("click", function () {
        goTo(parseInt(dot.getAttribute("data-hero-dot"), 10));
        start();
      });
    });

    hero.addEventListener("mouseenter", stop);
    hero.addEventListener("mouseleave", start);
    hero.addEventListener("focusin", stop);
    hero.addEventListener("focusout", function (e) {
      if (!hero.contains(e.relatedTarget)) start();
    });

    /* Belt and braces: if the first slide's onerror fires after this has
       already started (a slow-resolving 404), stop rotating and let the
       CSS fallback take over rather than crossfading an empty layer. */
    if (window.MutationObserver) {
      var mo = new MutationObserver(function () {
        if (hero.classList.contains("hero--no-photo")) {
          stop();
          mo.disconnect();
        }
      });
      mo.observe(hero, { attributes: true, attributeFilter: ["class"] });
    }

    goTo(0);
    start();
  }

  /* ---------- Wall reveal: homepage product cards fade up once ----------
     sections/featured-collection.liquid only adds data-wall-reveal when
     template.name == 'index', so this never runs on collection/search
     grids. Stagger is computed per IntersectionObserver callback batch
     (roughly "this row just entered the viewport"), not by the card's
     absolute position in the grid, so it can never run past the 300ms
     per-row cap the brief sets regardless of how far down the wall a
     card sits. */
  function initWallReveal() {
    var cards = document.querySelectorAll("[data-wall-reveal] .product-card");
    if (!cards.length) return;
    var reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !("IntersectionObserver" in window)) {
      cards.forEach(function (card) { card.classList.add("is-revealed"); });
      return;
    }
    var observer = new IntersectionObserver(function (entries, obs) {
      var entering = entries.filter(function (entry) { return entry.isIntersecting; });
      entering.forEach(function (entry, i) {
        var delay = Math.min(i, 7) * 40;
        entry.target.style.transitionDelay = delay + "ms";
        entry.target.classList.add("is-revealed");
        obs.unobserve(entry.target);
      });
    }, { threshold: 0.15, rootMargin: "0px 0px -10% 0px" });
    cards.forEach(function (card) { observer.observe(card); });
  }

  /* ---------- Header nav dropdowns ---------- */

  /* Called since 3ff740d (2026-08-18) but never written, so it threw a
     ReferenceError on every page load -- and because it is the SECOND call in
     the DOMContentLoaded block, every init after it never ran. Facets, the
     rudeness control, AI search, gallery, quantity, variant picker, cart,
     recommendations and the hero carousel were all dead site-wide for two
     days.

     The nav is <details>, so it opens and closes with no JS at all. This is
     enhancement only, matching the file's stated contract: close the other
     desktop dropdowns when one opens, and close on outside click or Escape.
     The mobile menu's groups share data-nav-dropdown but are a deliberate
     accordion inside an already-open panel, so they are left alone. */

  function initNavDropdowns() {
    var desktop = Array.prototype.slice.call(
      document.querySelectorAll(".site-header__nav [data-nav-dropdown]"));
    if (!desktop.length) return;

    function closeAll(except) {
      desktop.forEach(function (d) { if (d !== except) d.open = false; });
    }

    desktop.forEach(function (d) {
      d.addEventListener("toggle", function () { if (d.open) closeAll(d); });
    });

    document.addEventListener("click", function (e) {
      if (!desktop.some(function (d) { return d.contains(e.target); })) closeAll(null);
    });

    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape") return;
      var open = desktop.filter(function (d) { return d.open; });
      if (!open.length) return;
      closeAll(null);
      var s = open[0].querySelector("summary");
      if (s) s.focus();
    });
  }

  /* ---------- Personalisation photo upload (plan W5b, ostrich-upload/) ----------
     A file input rendered per photo-kind slot (sections/product-main.liquid)
     uploads on change to the Worker, shows a thumbnail, and writes the
     returned url into a hidden `properties[<Label>]` field the cart form
     already submits. Add-to-cart is refused - never silently submitted -
     while any required photo slot has no url yet: a disabled-button hint
     for the eye, and a submit-time guard as the actual gate (matching
     initVariantPicker's own separate, unrelated use of `.disabled` on the
     same button - this never re-enables a button initVariantPicker disabled
     for its own reason, it only ever ADDS a reason of its own). */

  function initPersonalisationUploads() {
    var form = document.querySelector("[data-product-form]");
    if (!form) return;
    var fileInputs = form.querySelectorAll('[data-personalisation-kind="photo"]');
    if (!fileInputs.length) return;
    var submitBtn = form.querySelector("[data-product-form-submit]");

    function pendingRequiredPhotos() {
      var pending = false;
      fileInputs.forEach(function (input) {
        if (!input.hasAttribute("required")) return;
        var wrap = input.closest(".option-group") || form;
        var urlInput = wrap.querySelector("[data-personalisation-photo-url]");
        if (!urlInput || !urlInput.value) pending = true;
      });
      return pending;
    }

    function refreshSubmitState() {
      if (!submitBtn) return;
      if (pendingRequiredPhotos()) {
        submitBtn.disabled = true;
      } else if (submitBtn.disabled && !submitBtn.getAttribute("data-variant-unavailable")) {
        submitBtn.disabled = false;
      }
    }

    fileInputs.forEach(function (input) {
      var wrap = input.closest(".option-group") || form;
      var statusEl = wrap.querySelector("[data-personalisation-photo-status]");
      var previewEl = wrap.querySelector("[data-personalisation-photo-preview]");
      var urlInput = wrap.querySelector("[data-personalisation-photo-url]");

      function setStatus(text) {
        if (statusEl) statusEl.textContent = text;
      }

      input.addEventListener("change", function () {
        var file = input.files && input.files[0];
        if (urlInput) urlInput.value = "";
        refreshSubmitState();
        if (!file) {
          setStatus("");
          if (previewEl) previewEl.hidden = true;
          return;
        }
        if (file.size > UPLOAD_MAX_BYTES) {
          setStatus("That picture is too large (15 MB limit). Choose a smaller one.");
          input.value = "";
          return;
        }
        if (file.type && file.type.indexOf("image/") !== 0) {
          setStatus("That file is not a picture. Choose an image.");
          input.value = "";
          return;
        }

        if (previewEl) {
          previewEl.src = URL.createObjectURL(file);
          previewEl.hidden = false;
        }
        setStatus("Uploading…");
        loadTurnstileScript();

        getTurnstileTokenFor("ostrich-upload").then(function (token) {
          var body = new FormData();
          body.set("file", file);
          if (token) body.set("turnstileToken", token);
          return fetch(UPLOAD_URL, { method: "POST", body: body }).then(function (res) {
            return res.json().then(function (data) {
              return { ok: res.ok, data: data };
            });
          });
        }).then(function (result) {
          if (!result.ok || !result.data || !result.data.url) {
            setStatus("Your photo could not be uploaded. Please try again.");
            if (urlInput) urlInput.value = "";
            refreshSubmitState();
            return;
          }
          var absoluteUrl = new URL(result.data.url, UPLOAD_URL).href;
          if (urlInput) urlInput.value = absoluteUrl;
          setStatus("Photo added.");
          refreshSubmitState();
        }).catch(function () {
          setStatus("Your photo could not be uploaded. Please try again.");
          if (urlInput) urlInput.value = "";
          refreshSubmitState();
        });
      });
    });

    refreshSubmitState();

    /* THE ACTUAL GATE. `.disabled` is a hint for the eye; this is what
       stops a submit from ever leaving with a required photo slot unfilled
       - never a silent add-to-cart for a mug nothing will print. */
    form.addEventListener("submit", function (e) {
      if (pendingRequiredPhotos()) {
        e.preventDefault();
        fileInputs.forEach(function (input) {
          if (!input.hasAttribute("required")) return;
          var wrap = input.closest(".option-group") || form;
          var urlInput = wrap.querySelector("[data-personalisation-photo-url]");
          var statusEl = wrap.querySelector("[data-personalisation-photo-status]");
          if (urlInput && !urlInput.value && statusEl && !statusEl.textContent) {
            statusEl.textContent = "Add a photo before adding this to your basket.";
          }
        });
      }
    });
  }

  /* ---------- /pages/hello: "show us your mug", a one-time 10% code -----
     The mug-box insert card's own QR code points here. Reuses UPLOAD_URL
     exactly as initPersonalisationUploads does above (same Turnstile
     bootstrap, same size/type checks, same worker), then calls OFFER_URL
     with the upload it just got back plus a SECOND, fresh Turnstile token
     - a token is single-use, so the same one that gated the upload cannot
     also gate the claim.

     NEVER "leave a review": the code is a thank-you for a shared photo,
     nothing here reads or writes anything review-shaped. See
     ostrich-upload/src/offer.ts and docs/incidents/ (the insert card
     proof) for why that line may not move. */
  function initHelloOffer() {
    var root = document.querySelector("[data-hello-offer]");
    if (!root) return;
    var input = root.querySelector("[data-hello-photo-input]");
    var statusEl = root.querySelector("[data-hello-photo-status]");
    var previewEl = root.querySelector("[data-hello-photo-preview]");
    var codeWrap = root.querySelector("[data-hello-offer-code]");
    var codeText = root.querySelector("[data-hello-offer-code-text]");
    var emailForm = root.querySelector("[data-hello-email-form]");
    var emailInput = emailForm && emailForm.querySelector("[data-hello-email-input]");
    var emailStatus = emailForm && emailForm.querySelector("[data-hello-email-status]");
    if (!input || !statusEl) return;

    var claimedCode = null;

    function setStatus(text) {
      statusEl.textContent = text;
    }

    function setEmailStatus(text) {
      if (emailStatus) emailStatus.textContent = text;
    }

    if (emailForm && emailInput) {
      emailForm.addEventListener("submit", function (e) {
        e.preventDefault();
        if (!claimedCode) return;
        var email = emailInput.value.trim();
        if (!email) return;
        setEmailStatus("Sending…");
        loadTurnstileScript();
        getTurnstileTokenFor("ostrich-email-code").then(function (token) {
          return fetch(EMAIL_CODE_URL, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ code: claimedCode, email: email, turnstileToken: token }),
          }).then(function (res) {
            return res.json().then(function (data) {
              return { ok: res.ok, status: res.status, data: data };
            });
          });
        }).then(function (result) {
          if (result.ok && result.data && result.data.sent) {
            setEmailStatus("Sent, check your inbox.");
          } else if (result.status === 429) {
            setEmailStatus("That code has already been emailed a few times. Check your inbox (and spam folder).");
          } else {
            setEmailStatus("Could not send that. Please try again.");
          }
        }).catch(function () {
          setEmailStatus("Could not send that. Please try again.");
        });
      });
    }

    input.addEventListener("change", function () {
      var file = input.files && input.files[0];
      if (codeWrap) codeWrap.hidden = true;
      if (emailForm) emailForm.hidden = true;
      claimedCode = null;
      setEmailStatus("");
      if (!file) {
        setStatus("");
        if (previewEl) previewEl.hidden = true;
        return;
      }
      if (file.size > UPLOAD_MAX_BYTES) {
        setStatus("That picture is too large (15 MB limit). Choose a smaller one.");
        input.value = "";
        return;
      }
      if (file.type && file.type.indexOf("image/") !== 0) {
        setStatus("That file is not a picture. Choose an image.");
        input.value = "";
        return;
      }

      if (previewEl) {
        previewEl.src = URL.createObjectURL(file);
        previewEl.hidden = false;
      }
      setStatus("Uploading…");
      loadTurnstileScript();

      getTurnstileTokenFor("ostrich-upload").then(function (uploadToken) {
        var body = new FormData();
        body.set("file", file);
        if (uploadToken) body.set("turnstileToken", uploadToken);
        return fetch(UPLOAD_URL, { method: "POST", body: body }).then(function (res) {
          return res.json().then(function (data) {
            return { ok: res.ok, data: data };
          });
        });
      }).then(function (uploaded) {
        if (!uploaded.ok || !uploaded.data || !uploaded.data.url) {
          setStatus("Your photo could not be uploaded. Please try again.");
          return null;
        }
        setStatus("Claiming your code…");
        return getTurnstileTokenFor("ostrich-offer").then(function (offerToken) {
          return fetch(OFFER_URL, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ uploadUrl: uploaded.data.url, turnstileToken: offerToken }),
          }).then(function (res) {
            return res.json().then(function (data) {
              return { ok: res.ok, status: res.status, data: data };
            });
          });
        });
      }).then(function (offered) {
        if (!offered) return;
        if (offered.ok && offered.data && offered.data.code) {
          setStatus("Photo added, thank you.");
          claimedCode = offered.data.code;
          if (codeWrap && codeText) {
            codeText.textContent = offered.data.code;
            codeWrap.hidden = false;
          }
          if (emailForm) emailForm.hidden = false;
        } else if (offered.status === 409) {
          setStatus("This photo has already claimed a code.");
        } else {
          setStatus("Your photo uploaded, but the code could not be issued. Please try again.");
        }
      }).catch(function () {
        setStatus("Something went wrong. Please try again.");
      });
    });
  }

  /* ---------- Personalisation: the one-line reason next to a disabled
     Add to basket (FIX-LIST item 5) ----------
     The button already looks inert (.btn[disabled], reduced opacity); this
     names WHAT is missing, in the buyer's own words. Runs on every input/
     change inside the product form, so it always gets the LAST word for
     any one event (a specific listener on an individual input, such as
     initPersonalisationUploads' own file-input handler above or
     initVariantPicker's option-input handler, fires first during the
     target phase; this delegated form-level listener fires after, during
     the bubble phase, and always recomputes the FULL truth) - it never
     needs to coordinate with those other gates, it just corrects the
     final state after each of them has had its say. Re-enabling only
     checks the button's own text against its sold-out/unavailable labels
     (the one artefact those other gates already set), so it never
     re-enables a button initVariantPicker disabled for stock reasons. */
  function initPersonalisationReason() {
    var form = document.querySelector("[data-product-form]");
    if (!form) return;
    var submitBtn = form.querySelector("[data-product-form-submit]");
    var reasonEl = form.querySelector("[data-personalisation-reason]");
    if (!submitBtn || !reasonEl) return;

    function missingReason() {
      var strings = getOstrichStrings();
      var photoInputs = form.querySelectorAll('[data-personalisation-kind="photo"][required]');
      for (var i = 0; i < photoInputs.length; i++) {
        var wrap = photoInputs[i].closest(".option-group") || form;
        var urlInput = wrap.querySelector("[data-personalisation-photo-url]");
        if (!urlInput || !urlInput.value) return strings.personalisationReasonPhoto || "";
      }
      var textInputs = form.querySelectorAll(
        "[data-personalisation-input][required], select[data-personalisation-kind='dropdown'][required]"
      );
      for (var j = 0; j < textInputs.length; j++) {
        if (!textInputs[j].value || !textInputs[j].value.trim()) return strings.personalisationReasonText || "";
      }
      return "";
    }

    function refresh() {
      var reason = missingReason();
      reasonEl.textContent = reason;
      if (reason) {
        submitBtn.disabled = true;
      } else if (
        submitBtn.disabled &&
        submitBtn.textContent !== submitBtn.getAttribute("data-label-sold-out") &&
        submitBtn.textContent !== submitBtn.getAttribute("data-label-unavailable")
      ) {
        submitBtn.disabled = false;
      }
    }

    form.addEventListener("input", refresh);
    form.addEventListener("change", refresh);
    refresh();
  }

  /* ---------- Cart page quantity change: submit form ----------
     The /cart page itself (sections/cart.liquid) still posts a normal
     form on a quantity change -- it never had a fetch/JSON path and this
     fix does not add one. initCartDrawer below is the separate,
     JS-fetch-driven line list that lives in the header-triggered drawer,
     not here. */

  function initCartForm() {
    var cartForm = document.querySelector("[data-cart-form]");
    if (!cartForm) return;
    cartForm.querySelectorAll("input[data-cart-qty]").forEach(function (input) {
      input.addEventListener("change", function () {
        cartForm.submit();
      });
    });
  }

  /* ---------- Sticky buy bar (do-now item 2, 2026-09-21, phones only) ----------
     Shows once the real Add to basket button (inside #ProductForm, above)
     has scrolled out of view; hides again the moment it is back in view.
     The cart drawer being open is handled entirely in CSS
     (body.cart-drawer-lock, the same class initCartDrawer already
     toggles) -- no second "is the drawer open" flag lives here. The bar's
     own button submits the SAME form via `form="ProductForm"`, so this
     never re-implements the add-to-cart request; it only decides when to
     show a second way to reach the one button that already does it. */
  function initStickyBuyBar() {
    var bar = document.querySelector("[data-sticky-buy-bar]");
    var mainBtn = document.querySelector("[data-product-form-submit]");
    if (!bar || !mainBtn || !("IntersectionObserver" in window)) return;

    function setHeightVar() {
      var h = bar.getBoundingClientRect().height;
      if (h > 0) document.documentElement.style.setProperty("--sticky-buy-bar-height", h + "px");
    }

    function show() {
      if (bar.classList.contains("is-visible")) return;
      bar.classList.add("is-visible");
      setHeightVar();
    }
    function hide() {
      bar.classList.remove("is-visible");
    }

    var observer = new IntersectionObserver(function (entries) {
      var entry = entries[entries.length - 1];
      if (!entry) return;
      if (entry.isIntersecting) {
        hide();
      } else {
        /* Whenever Add to basket is off screen, above OR below: on a phone
           the bar is on the first screen, before any scroll (Alan,
           2026-09-29, option B of the first-screen mockup: "B looks
           good"). Until then it waited for a visitor to scroll past the
           button once, so a phone's first screen had nothing to buy. The
           bar is phone-only (theme.css hides it from 56rem). */
        show();
      }
    });
    observer.observe(mainBtn);

    window.addEventListener("resize", setHeightVar);
  }

  /* ---------- Cart drawer (Alan, 2026-09-19: "Yes, a drawer") ----------
     Today Add to basket posted the product form and, on the audit's own
     first click, gave no confirmation at all; on the second it navigated
     to /cart. This intercepts that same submit AFTER every existing slot
     handler has had its turn (initPersonalisationUploads' own submit
     listener, registered earlier in the DOMContentLoaded list below,
     still runs first and still owns the one real gate -- a required
     photo with no uploaded url yet -- so this only ever sees a submit
     that gate already let through, and checks `e.defaultPrevented` to be
     certain of that rather than re-deriving the same rule a second time).
     `new FormData(form)` reads the CURRENT state of every field, so the
     photo Worker's returned url (written into a hidden
     properties[<Label>] input on upload) rides along exactly as it would
     in a normal POST -- nothing here re-implements the slot logic in
     sections/product-main.liquid, it only defers to whatever that markup
     and its existing handlers have already put on the form.

     Falls back to a normal form submission -- not a caught error shown in
     the drawer -- on ANY failure: fetch throwing (offline, the Worker/
     Shopify unreachable), a non-2xx response (Shopify itself refusing the
     add, e.g. a stock change since page load), or no <dialog>.showModal
     support at all. That fallback is exactly today's behaviour, so a
     buyer on a browser this never got a chance to run on, or hitting
     a real network fault, still completes the purchase. */

  var cartDrawerLastTrigger = null;
  var CART_DRAWER_ADDED_HOLD_MS = 1500;

  function cartDrawerRefs() {
    var dialog = document.querySelector("[data-cart-drawer]");
    if (!dialog) return null;
    return {
      dialog: dialog,
      body: dialog.querySelector("[data-cart-drawer-body]"),
      lines: dialog.querySelector("[data-cart-drawer-lines]"),
      empty: dialog.querySelector("[data-cart-drawer-empty]"),
      footer: dialog.querySelector("[data-cart-drawer-footer]"),
      subtotal: dialog.querySelector("[data-cart-drawer-subtotal]"),
      shipping: dialog.querySelector("[data-cart-drawer-shipping]"),
      recs: dialog.querySelector("[data-cart-drawer-recs]"),
      template: dialog.querySelector("[data-cart-drawer-line-template]"),
      closeBtn: dialog.querySelector("[data-cart-drawer-close]"),
      keepBrowsing: dialog.querySelector("[data-cart-drawer-keep-browsing]"),
    };
  }

  /* ---------- Cart drawer: "You might also like" ----------
     Alan, 2026-09-25: "do everything we're able with basic" -- the
     checkout page itself cannot carry upsells on Shopify Basic, so the
     drawer carries them instead.

     Source is Shopify's OWN recommendations, through OUR section
     (sections/product-recommendations.liquid) -- the exact mechanism
     fetchShopifyRecommendationsFallback already uses above, so every card
     is server-rendered through snippets/tier-guard.liquid (never ruder
     than the FIRST line's own product) and the rude-mask markup
     matches every other card on the site. section_id is the static file
     name (see cart-drawer.liquid's own comment on data-cart-recs-url);
     product_id is appended per fetch, below, since it depends on whatever
     the basket's first line is at fetch time. */
  var CART_DRAWER_RECS_SECTION_ID = "product-recommendations";
  var cartDrawerLastRecsProductId = null;
  /* Same incrementing-token practice as initLivePreview's seq/mine guard
     and assets/basket-delivery.js's inFlight token: a fast add-then-remove
     can start a recs fetch for product A and then, before it resolves, a
     second one for product B. Nothing cancels the first, so whichever
     response lands last would otherwise win the innerHTML write even if
     it is the stale one. */
  var cartDrawerRecsToken = null;

  function cartDrawerHideRecs(refs) {
    cartDrawerRecsToken = null; /* no fetch in flight owns the drawer any more */
    if (!refs.recs) return;
    refs.recs.hidden = true;
    refs.recs.innerHTML = "";
  }

  /* Same DOMParser-and-look-up-by-id practice as
     fetchShopifyRecommendationsFallback: nothing here is ever assigned to
     the live DOM except an element that was itself parsed out of our own
     section's HTML response -- never a raw string built from it. The two
     things the fallback function doesn't need to do are done on the
     PARSED copy before anything touches the real DOM: dropping a card for
     a product already in the basket (rule: never suggest what's already
     in it), and swapping the section's own <h2> for an <h3> carrying the
     locale heading off the dialog's data-cart-recs-heading attribute --
     the drawer's h2 is the dialog's own title, so a second h2 inside it
     would be a skipped/duplicated heading level. */
  function fetchCartDrawerRecommendations(refs, productId, basketHandles) {
    if (!refs.recs) return;
    var urlBase = refs.dialog.getAttribute("data-cart-recs-url");
    if (!urlBase || !productId) {
      cartDrawerHideRecs(refs);
      return;
    }
    var token = {};
    cartDrawerRecsToken = token; /* this call now owns the drawer; any older in-flight fetch's response is discarded below */
    var url = urlBase + "&product_id=" + encodeURIComponent(productId);
    fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        if (cartDrawerRecsToken !== token) return; /* a newer fetch has started since; its answer wins */
        var doc = new DOMParser().parseFromString(html, "text/html");
        var section = doc.getElementById("ProductRecommendations-" + CART_DRAWER_RECS_SECTION_ID);
        var list = section && section.querySelector(".product-grid");
        if (!list) {
          cartDrawerHideRecs(refs);
          return;
        }
        Array.prototype.forEach.call(list.querySelectorAll("a.product-card"), function (card) {
          var handle = cardHandleFromCard(card);
          if (handle && basketHandles && basketHandles[handle]) {
            var li = card.closest("li");
            if (li) li.remove();
          }
        });
        if (!list.children.length) {
          cartDrawerHideRecs(refs);
          return;
        }
        var heading = refs.dialog.getAttribute("data-cart-recs-heading") || "";
        refs.recs.innerHTML = "";
        var h3 = document.createElement("h3");
        h3.className = "cart-drawer__recs-heading";
        h3.textContent = heading;
        list.className = "cart-drawer__recs-grid";
        refs.recs.appendChild(h3);
        refs.recs.appendChild(list); /* moved, not re-parsed -- still the section's own markup */
        refs.recs.hidden = false;
        initCardMedia(refs.recs);
        initCardSpinHover(refs.recs);
        initCardMask(refs.recs);
        initDeliveryDates(); /* re-tick now these cards' [data-delivery-date] elements exist */
      })
      .catch(function () {
        if (cartDrawerRecsToken !== token) return; /* superseded; the newer call's own catch/success owns the drawer now */
        cartDrawerHideRecs(refs); /* progressive: a failed fetch leaves the drawer exactly as it was */
      });
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }

  var CART_DRAWER_INERT_SUPPORTED = "inert" in HTMLElement.prototype;

  /* `inert` on every OTHER direct child of <body> while the drawer is
     open -- belt and braces alongside showModal()'s own top-layer focus
     trap (which already keeps focus and pointer/AT interaction off the
     rest of the page on its own; this just says so outright, per the
     brief's own checklist, and costs nothing where a browser lacks
     `inert` -- the feature check just skips it). */
  function setBackgroundInert(dialog, on) {
    if (!CART_DRAWER_INERT_SUPPORTED) return;
    Array.prototype.forEach.call(document.body.children, function (child) {
      if (child === dialog) return;
      if (on) {
        child.setAttribute("inert", "");
      } else {
        child.removeAttribute("inert");
      }
    });
  }

  function updateHeaderCartCount(count) {
    document.querySelectorAll("[data-cart-count]").forEach(function (el) {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
    document.querySelectorAll("[data-cart-drawer-open]").forEach(function (link) {
      var label = link.getAttribute("data-header-cart-label") || "";
      link.setAttribute("aria-label", label + ": " + count);
    });
  }

  function fetchCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } }).then(function (res) {
      if (!res.ok) throw new Error("cart_fetch_failed");
      return res.json();
    });
  }

  /* Same fallback the AI-search/recommendations title logic already uses
     (normalizeStorefrontNode's titleFallback) -- strip the " | SEO tail"
     and a trailing " Mug", for the instant before (or in place of) the
     Storefront upgrade below resolves the real spoken_line/caption. */
  function cartDrawerFallbackTitle(item) {
    var raw = item.product_title || item.title || "";
    return raw.split(" | ")[0].trim().replace(/ Mug$/, "");
  }

  /* What the buyer typed or chose for a personalised line -- the same rule
     as snippets/line-properties.liquid (skip "_" keys and blanks; a photo
     URL shows as a link, never the URL). Keep the two in step. */
  function fillLineProperties(el, properties) {
    el.innerHTML = "";
    var shown = 0;
    Object.keys(properties || {}).forEach(function (label) {
      var value = properties[label];
      if (label.charAt(0) === "_" || value == null || String(value).trim() === "") return;
      var row = document.createElement("div");
      var dt = document.createElement("dt");
      dt.textContent = label + ":";
      var dd = document.createElement("dd");
      if (String(value).indexOf("://") !== -1) {
        var a = document.createElement("a");
        a.href = String(value);
        a.target = "_blank";
        a.rel = "noopener";
        a.textContent = "Photo added";
        dd.appendChild(a);
      } else {
        dd.textContent = String(value);
      }
      row.appendChild(dt);
      row.appendChild(dd);
      el.appendChild(row);
      shown += 1;
    });
    el.hidden = shown === 0;
  }

  function buildCartDrawerLine(template, item, currency) {
    var frag = template.content.cloneNode(true);
    var li = frag.querySelector(".cart-drawer__line");
    li.setAttribute("data-line-key", item.key);
    li.setAttribute("data-line-product-id", String(item.product_id));

    var imageLink = frag.querySelector("[data-line-image-link]");
    var image = frag.querySelector("[data-line-image]");
    if (item.image) {
      var sep = item.image.indexOf("?") !== -1 ? "&" : "?";
      image.src = item.image + sep + "width=160";
    } else if (imageLink) {
      imageLink.remove();
    }

    var titleLink = frag.querySelector("[data-line-title-link]");
    titleLink.href = item.url;
    titleLink.textContent = cartDrawerFallbackTitle(item);

    var variantEl = frag.querySelector("[data-line-variant]");
    if (item.variant_title && item.variant_title !== "Default Title") {
      variantEl.textContent = item.variant_title;
      variantEl.hidden = false;
    }

    var propsEl = frag.querySelector("[data-line-properties]");
    if (propsEl) fillLineProperties(propsEl, item.properties);

    var qtyInput = frag.querySelector("[data-line-qty-input]");
    qtyInput.value = item.quantity;

    var priceEl = frag.querySelector("[data-line-price]");
    priceEl.textContent = formatMoney(item.final_line_price / 100, currency);

    return frag;
  }

  /* Upgrades each line's fallback title to the real spoken_line/caption,
     the same two metafields and the same precedence
     (common/design_facets.py::display_line(), mirrored client-side
     already by normalizeStorefrontNode above) -- one batched Storefront
     query for every distinct product in the cart, never one per line.
     Silent no-op with the fallback left standing if Storefront config is
     missing (no token rendered - see getStorefrontConfig) or the request
     fails; this is a progressive upgrade, never the only way a title is
     shown. */
  function upgradeCartDrawerTitles(linesEl, items) {
    var cfg = getStorefrontConfig();
    if (!cfg || !items.length) return;
    var ids = [];
    var seen = {};
    items.forEach(function (item) {
      var gid = "gid://shopify/Product/" + item.product_id;
      if (!seen[gid]) {
        seen[gid] = true;
        ids.push(gid);
      }
    });
    var query =
      "query OstrichCartTitles($ids: [ID!]!) { nodes(ids: $ids) { ... on Product { id " +
      'metafields(identifiers: [{namespace: "ostrich", key: "caption"}, {namespace: "ostrich", key: "spoken_line"}]) { key value } } } }';
    fetch("https://" + cfg.shop + "/api/" + cfg.apiVersion + "/graphql.json", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "X-Shopify-Storefront-Access-Token": cfg.token,
      },
      body: JSON.stringify({ query: query, variables: { ids: ids } }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error("storefront_bad_status");
        return res.json();
      })
      .then(function (data) {
        if (data.errors && data.errors.length) throw new Error("storefront_graphql_error");
        var nodes = (data.data && data.data.nodes) || [];
        nodes.forEach(function (node) {
          if (!node) return;
          var byKey = {};
          (node.metafields || []).forEach(function (mf) {
            if (mf) byKey[mf.key] = mf.value;
          });
          var caption = byKey.caption ? byKey.caption.replace(/\s+/g, " ").trim() : "";
          var spokenLine = byKey.spoken_line ? byKey.spoken_line.replace(/\s+/g, " ").trim() : "";
          var resolved = spokenLine || caption;
          if (!resolved) return;
          var productId = node.id.replace("gid://shopify/Product/", "");
          var line = linesEl.querySelector('[data-line-product-id="' + productId + '"] [data-line-title]');
          if (line) line.textContent = resolved;
        });
      })
      .catch(function () {
        /* Fallback title already rendered; nothing more to do. */
      });
  }

  /* Replicates snippets/shipping-message.liquid's own short-line
     threshold check (variant: 'short') against the CURRENT basket total,
     reading the two settings and two possible strings off the dialog's
     own data attributes (see snippets/cart-drawer.liquid) rather than
     restating either as a JS literal. Only ever rewrites the <span> of
     text inside the already-server-rendered line, so the icon markup
     that snippet emits is never disturbed. */
  function updateDrawerShipping(dialog, shippingEl, totalPriceMinor) {
    if (!shippingEl) return;
    var span = shippingEl.querySelector("span");
    if (!span) return;
    var freeAll = dialog.getAttribute("data-shipping-free-all") === "true";
    var threshold = parseInt(dialog.getAttribute("data-shipping-threshold") || "", 10);
    var useTracked24 = freeAll && !isNaN(threshold) && threshold > 0 && totalPriceMinor >= threshold;
    var text = useTracked24
      ? dialog.getAttribute("data-shipping-free-short-tracked24")
      : dialog.getAttribute("data-shipping-free-short");
    if (text) span.textContent = text;
  }

  function renderCartDrawer(refs, cart) {
    updateHeaderCartCount(cart.item_count);
    if (!cart.items.length) {
      refs.lines.innerHTML = "";
      if (refs.empty) refs.empty.hidden = false;
      if (refs.footer) refs.footer.hidden = true;
      cartDrawerLastRecsProductId = null;
      cartDrawerHideRecs(refs);
      return;
    }
    if (refs.empty) refs.empty.hidden = true;
    if (refs.footer) refs.footer.hidden = false;
    if (refs.template && refs.template.content) {
      refs.lines.innerHTML = "";
      cart.items.forEach(function (item) {
        refs.lines.appendChild(buildCartDrawerLine(refs.template, item, cart.currency));
      });
    }
    if (refs.subtotal) {
      refs.subtotal.textContent = formatMoney(cart.total_price / 100, cart.currency);
    }
    updateDrawerShipping(refs.dialog, refs.shipping, cart.total_price);
    upgradeCartDrawerTitles(refs.lines, cart.items);
    // For listeners outside this file (assets/basket-delivery.js).
    document.dispatchEvent(new CustomEvent("ostrich:cart-rendered", { detail: { cart: cart } }));

    /* Re-fetch only when the FIRST line's own product actually changed --
       a quantity step on the same product (by far the most common
       mutation) must not re-hit the recommendations endpoint every click. */
    var firstProductId = cart.items[0].product_id;
    if (firstProductId !== cartDrawerLastRecsProductId) {
      cartDrawerLastRecsProductId = firstProductId;
      var basketHandles = {};
      cart.items.forEach(function (item) {
        if (item.handle) basketHandles[item.handle] = true;
      });
      fetchCartDrawerRecommendations(refs, firstProductId, basketHandles);
    }
  }

  function changeCartDrawerLine(refs, key, quantity) {
    fetch("/cart/change.js", {
      method: "POST",
      headers: { "content-type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: quantity }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error("cart_change_failed");
        return res.json();
      })
      .then(function (cart) {
        renderCartDrawer(refs, cart);
      })
      .catch(function () {
        /* The drawer's own state may now disagree with Shopify's; the
           full cart page always reflects the true state on load, so land
           there rather than leave a stale quantity showing. */
        window.location.href = "/cart";
      });
  }

  function openCartDrawer(refs, trigger) {
    if (refs.dialog.open) return;
    if (typeof refs.dialog.showModal !== "function") return;
    cartDrawerLastTrigger = trigger || null;
    try {
      refs.dialog.showModal();
    } catch (e) {
      return;
    }
    document.body.classList.add("cart-drawer-lock");
    setBackgroundInert(refs.dialog, true);
    if (prefersReducedMotion()) {
      refs.dialog.classList.add("is-open");
    } else {
      /* Two rAFs: the first lets the dialog's own opening paint (still at
         its resting transform: translateX(100%)) land before the second
         one adds `is-open`, so the browser has something to transition
         FROM rather than jumping straight to the open state. */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          refs.dialog.classList.add("is-open");
        });
      });
    }
  }

  function initCartDrawer() {
    var refs = cartDrawerRefs();
    if (!refs || !refs.lines || !refs.template) return;

    refs.dialog.addEventListener("close", function () {
      refs.dialog.classList.remove("is-open");
      document.body.classList.remove("cart-drawer-lock");
      setBackgroundInert(refs.dialog, false);
      if (cartDrawerLastTrigger && typeof cartDrawerLastTrigger.focus === "function") {
        cartDrawerLastTrigger.focus();
      }
      cartDrawerLastTrigger = null;
    });

    /* Click on the backdrop (the dialog element itself, outside
       .cart-drawer__panel) dismisses it -- not asked for by name, but
       the conventional native-<dialog> pattern, and cheap. */
    refs.dialog.addEventListener("click", function (e) {
      if (e.target === refs.dialog) refs.dialog.close();
    });

    if (refs.closeBtn) refs.closeBtn.addEventListener("click", function () { refs.dialog.close(); });
    if (refs.keepBrowsing) refs.keepBrowsing.addEventListener("click", function () { refs.dialog.close(); });

    /* Quantity steppers and Remove are delegated on the list container,
       not bound per line: every render above replaces the list wholesale
       (innerHTML = ""), so a per-line listener would need re-attaching on
       every single mutation -- one delegated pair here never does. */
    refs.lines.addEventListener("click", function (e) {
      var line = e.target.closest("[data-line-key]");
      if (!line) return;
      var key = line.getAttribute("data-line-key");
      if (e.target.closest("[data-line-remove]")) {
        changeCartDrawerLine(refs, key, 0);
        return;
      }
      var minus = e.target.closest("[data-qty-minus]");
      var plus = e.target.closest("[data-qty-plus]");
      if (!minus && !plus) return;
      var input = line.querySelector("[data-line-qty-input]");
      var current = parseInt(input.value, 10) || 1;
      var next = Math.max(0, current + (plus ? 1 : -1));
      changeCartDrawerLine(refs, key, next);
    });
    refs.lines.addEventListener("change", function (e) {
      var input = e.target.closest("[data-line-qty-input]");
      if (!input) return;
      var line = input.closest("[data-line-key]");
      if (!line) return;
      var next = Math.max(0, parseInt(input.value, 10) || 0);
      changeCartDrawerLine(refs, line.getAttribute("data-line-key"), next);
    });

    document.querySelectorAll("[data-cart-drawer-open]").forEach(function (opener) {
      opener.addEventListener("click", function (e) {
        if (typeof refs.dialog.showModal !== "function") return; /* let it navigate to /cart */
        e.preventDefault();
        var trigger = opener;
        fetchCart()
          .then(function (cart) {
            renderCartDrawer(refs, cart);
            openCartDrawer(refs, trigger);
          })
          .catch(function () {
            window.location.href = "/cart";
          });
      });
    });

    var form = document.querySelector("[data-product-form]");
    if (!form) return;
    form.addEventListener("submit", function (e) {
      /* An earlier listener on this same form (initPersonalisationUploads,
         registered before this one runs - see the DOMContentLoaded list
         below) already prevented the default for a real reason - a
         required photo slot with no uploaded url yet. Never re-derive
         that rule here; just defer to it. */
      if (e.defaultPrevented) return;
      if (typeof refs.dialog.showModal !== "function") return;
      e.preventDefault();

      var submitBtn = form.querySelector("[data-product-form-submit]");
      var originalLabel = submitBtn ? submitBtn.textContent : "";
      var formData = new FormData(form);
      /* The raw file (up to 15MB) never leaves this device a second time
         - the Worker already has it, and the hidden properties[<Label>]
         input it wrote carries the URL that matters to Shopify. */
      form.querySelectorAll('input[type="file"]').forEach(function (fileInput) {
        if (fileInput.name) formData.delete(fileInput.name);
      });

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = submitBtn.getAttribute("data-label-adding") || originalLabel;
      }

      fetch("/cart/add.js", { method: "POST", body: formData, headers: { Accept: "application/json" } })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (data) {
              throw new Error((data && data.description) || "cart_add_failed");
            });
          }
          return fetchCart();
        })
        .then(function (cart) {
          renderCartDrawer(refs, cart);
          openCartDrawer(refs, submitBtn || form);
          if (submitBtn) {
            submitBtn.textContent = submitBtn.getAttribute("data-label-added") || originalLabel;
            setTimeout(function () {
              submitBtn.textContent = originalLabel;
              submitBtn.disabled = false;
            }, CART_DRAWER_ADDED_HOLD_MS);
          }
        })
        .catch(function () {
          /* Network failure, or Shopify itself refused the add (e.g. a
             stock change since page load) - fall back to the plain POST,
             today's exact behaviour, rather than strand the buyer on a
             busy button with no basket and no explanation. */
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalLabel;
          }
          form.submit();
        });
    });
  }

  /* Each init is isolated. The missing initNavDropdowns above proved the
     failure mode: one throw in this list silently killed every feature below
     it, and the page still looked fine because nothing here logs. Now a
     broken init costs its own feature and names itself in the console.

     Audit fix (FIX-LIST.md #10): every init already bails on its own via an
     early-return querySelector against its own root element - that never
     showed up in `unused-javascript` (that audit counts wasted bytes
     inside a file, not wasted calls) but it is real per-page work: on the
     homepage 6 of these already found nothing, on a collection page 7-8,
     on the product page 4-5. Root selector is now checked ONCE here, at
     the dispatch table, before the init function is even entered - a
     template with no [data-facets-form] never runs facets()'s body (its
     second querySelector, its dropdown-toggle wiring) at all, it just
     fails the one check below and moves on. Each function keeps its own
     internal guard too (defence in depth, and every function still works
     correctly if called directly, e.g. from a test) - this list only
     decides whether to make that call.

     searchResultsEnhance's own root, #AiSearchInline[data-initial-query],
     matches on every /search page load (data-initial-query is always
     rendered, even empty - sections/search-results.liquid) but the
     function itself checks the attribute is non-empty before doing any
     work, same defence-in-depth as every other init here. */

  /* ---------- Homepage 2026-09-25: the gift finder ----------
     sections/hero-finder.liquid is a plain GET form against the all-products
     collection with every select named after a storefront filter, so it
     works with no script. This upgrade sends the visitor to the recipient's
     OWN collection (mugs-for-dad rather than all?recipient=dad), read off
     the option's data-url that Liquid filled from the collection, keeps the
     occasion and tone as filters, and turns the rude switch on when
     "properly rude" was chosen so the landing page's covers are lifted.
     The key written is RUDE_SWITCH_KEY, the one initRudeSwitch owns. */
  function initHeroFinder() {
    document.querySelectorAll("[data-finder]").forEach(function (form) {
      /* The stack answers the finder: show the chosen person's own four
         mugs (one pre-rendered set per recipient, hidden until asked for),
         falling back to the editor's default set when no set exists. */
      var stack = document.querySelector("[data-stack]");
      var who0 = form.querySelector("[data-finder-who]");
      if (stack && who0) {
        who0.addEventListener("change", function () {
          var sets = stack.querySelectorAll("[data-stack-for]");
          var wanted = stack.querySelector('[data-stack-for="' + who0.value + '"]');
          if (!wanted || !wanted.querySelector("img")) wanted = stack.querySelector('[data-stack-for=""]');
          var opt = who0.options[who0.selectedIndex];
          var label = document.querySelector("[data-shelf-label]");
          if (label && opt && opt.getAttribute("data-shelf-label")) label.textContent = opt.getAttribute("data-shelf-label");
          var countEl = form.querySelector("[data-finder-count]"), countLabel = form.querySelector("[data-finder-count-label]");
          if (countEl && opt && opt.getAttribute("data-count")) {
            countEl.textContent = Number(opt.getAttribute("data-count")).toLocaleString("en-GB");
            if (countLabel) countLabel.textContent = opt.getAttribute("data-count-label") || "";
          }
          var current = stack.querySelector("[data-stack-for].is-active");
          var reduce = window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches;
          function show() {
            sets.forEach(function (el) { el.hidden = true; el.classList.remove("is-active", "is-leaving"); });
            if (wanted) { wanted.hidden = false; wanted.classList.add("is-active"); }
          }
          if (current && current !== wanted && !reduce && current.classList.contains("hero-shelf__set")) {
            current.classList.add("is-leaving");
            setTimeout(show, 230);
          } else {
            show();
          }
        });
      }
      form.addEventListener("submit", function (event) {
        var who = form.querySelector("[data-finder-who]");
        var occ = form.querySelector("[data-finder-occasion]");
        var tone = form.querySelector("[data-finder-tone]");
        if (!who || !occ || !tone) return;
        var opt = who.options[who.selectedIndex];
        var base = opt ? opt.getAttribute("data-url") : null;
        if (!base) return; /* no collection for this person: the plain submit stands */
        event.preventDefault();
        var params = [];
        if (occ.value) params.push(encodeURIComponent(occ.name) + "=" + encodeURIComponent(occ.value));
        if (tone.value) params.push(encodeURIComponent(tone.name) + "=" + encodeURIComponent(tone.value));
        if (tone.value === "rude" && !toneSwitchOff()) {
          document.documentElement.setAttribute("data-show-rude", "true");
          setRudeSwitchesChecked(true);
          try { localStorage.setItem(RUDE_SWITCH_KEY, "1"); } catch (e) {}
        }
        window.location.href = base + (params.length ? "?" + params.join("&") : "");
      });
    });
  }

  /* ---------- Homepage v3, The Shelf (2026-09-25): the wall clock ----------
     Decorative twin of [data-dispatch-tag]: hands at the visitor's clock,
     an orange wedge from now to the cut-off while today is a despatch day
     with time left, nothing after. Same despatch days and cut-off as every
     other time claim on the site (isDispatchDay, data-cutoff-hour). */
  function initWallClock() {
    var clocks = document.querySelectorAll("[data-wall-clock]");
    if (!clocks.length) return;
    function pol(a, r) { var t = (a - 90) * Math.PI / 180; return [50 + r * Math.cos(t), 50 + r * Math.sin(t)]; }
    function wedge(a1, a2) {
      var p1 = pol(a1, 43), p2 = pol(a2, 43);
      var sweep = ((a2 - a1) % 360 + 360) % 360;
      if (sweep < 0.5) return "";
      var big = sweep > 180 ? 1 : 0;
      return "M50 50 L" + p1[0].toFixed(2) + " " + p1[1].toFixed(2) + " A43 43 0 " + big + " 1 " + p2[0].toFixed(2) + " " + p2[1].toFixed(2) + " Z";
    }
    function tick() {
      var now = new Date();
      clocks.forEach(function (svg) {
        var cutoff = cutoffHourFor(now, Number(svg.getAttribute("data-cutoff-hour")));
        var hour = svg.querySelector("[data-clock-hour]"), min = svg.querySelector("[data-clock-min]"), w = svg.querySelector("[data-clock-wedge]");
        var mh = (now.getHours() % 12) * 30 + now.getMinutes() * 0.5, mm = now.getMinutes() * 6;
        if (hour) hour.setAttribute("transform", "rotate(" + mh + " 50 50)");
        if (min) min.setAttribute("transform", "rotate(" + mm + " 50 50)");
        if (isNaN(cutoff)) return;
        /* the orange marker and its label sit at the day's own cut-off (2pm, or 10am on a Saturday) */
        var ang = (cutoff % 12) * 30;
        var tickEl = svg.querySelector("[data-clock-cutoff]"), txt = svg.querySelector("[data-clock-cutoff-text]");
        if (tickEl) tickEl.setAttribute("transform", "rotate(" + (ang - 60) + " 50 50)");
        if (txt) {
          var rad = ang * Math.PI / 180;
          txt.setAttribute("x", (50 + 22 * Math.sin(rad)).toFixed(1));
          txt.setAttribute("y", (52.5 - 22 * Math.cos(rad)).toFixed(1));
          txt.textContent = (cutoff % 12 || 12) + (cutoff >= 12 ? "pm" : "am");
        }
        if (!w) return;
        var open = isDispatchDay(now) && (now.getHours() * 60 + now.getMinutes()) < cutoff * 60;
        w.setAttribute("d", open ? wedge(mh, ang) : "");
      });
    }
    tick();
    setInterval(tick, 30000);
  }

  /* ---------- Homepage v3: the fridge note ----------
     Real dates, never adjectives: the day the gift is posted if you order
     now (today before the cut-off on a despatch day, else the next despatch
     day) and the day it arrives (transit counted in delivery days from the
     despatch day, common/lead_time.py's rule, the same arithmetic as the
     product page's delivery date). */
  function initFridgeNote() {
    var notes = document.querySelectorAll("[data-fridge-note]");
    if (!notes.length) return;
    var fmt;
    try { fmt = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short" }); } catch (e) { return; }
    function tick() {
      var now = new Date();
      notes.forEach(function (note) {
        var cutoff = Number(note.getAttribute("data-cutoff-hour")) || 14;
        var transit = Number(note.getAttribute("data-transit-days")) || 2;
        var post = new Date(now.getTime());
        if (!(isDispatchDay(post) && now.getHours() < cutoffHourFor(post, cutoff))) { post.setDate(post.getDate() + 1); post = nextDispatchDay(post); }
        var arrive = addDeliveryDays(post, transit);
        var p = note.querySelector("[data-fridge-post]"), a = note.querySelector("[data-fridge-arrive]");
        if (p) p.textContent = fmt.format(post);
        if (a) a.textContent = fmt.format(arrive);
      });
    }
    tick();
    setInterval(tick, 60000);
  }

  /* ---------- Homepage 2026-09-25: newsletter forms without a reload ----------
     The email band and the popup are Shopify customer forms (form_type
     "customer", action /contact) exactly like the footer's. Submitting
     them with fetch keeps the visitor where they are and shows the code
     in place. Shopify answers a good submission with a redirect back to
     the page carrying ?customer_posted=true; anything else (a validation
     error, or a redirect to /challenge when Shopify wants a bot check)
     falls back to the plain submit, so the visitor always gets an answer.
     A signup is remembered in localStorage so the popup never asks again. */
  var NEWSLETTER_KEY = "ostrich:newsletter";
  function newsletterState() {
    try { return localStorage.getItem(NEWSLETTER_KEY) || ""; } catch (e) { return ""; }
  }
  function setNewsletterState(value) {
    try { localStorage.setItem(NEWSLETTER_KEY, value); } catch (e) {}
  }
  function showNewsletterDone(form) {
    Array.prototype.forEach.call(form.elements, function (el) {
      if (el.type !== "hidden") el.hidden = true;
    });
    form.querySelectorAll(".newsletter-honeypot, .email-band__consent, .newsletter-error").forEach(function (el) { el.hidden = true; });
    var done = form.querySelector("[data-newsletter-done]");
    if (done) { done.hidden = false; done.focus && done.setAttribute("tabindex", "-1"); done.focus(); }
    setNewsletterState("done");
  }
  function initNewsletterAjax() {
    document.querySelectorAll("[data-newsletter-ajax]").forEach(function (form) {
      form.addEventListener("submit", function (event) {
        var trap = form.querySelector("[data-newsletter-trap]");
        if (trap && trap.value) { event.preventDefault(); return; }
        if (!window.fetch || !window.FormData) return;
        event.preventDefault();
        var errorEl = form.querySelector("[data-newsletter-error]");
        var button = form.querySelector('button[type="submit"]');
        if (button) { button.disabled = true; button.setAttribute("aria-busy", "true"); }
        fetch(form.action, { method: "POST", body: new FormData(form), credentials: "same-origin", redirect: "follow" })
          .then(function (res) {
            var landed = res.url || "";
            if (landed.indexOf("customer_posted=true") !== -1) { showNewsletterDone(form); return; }
            if (landed.indexOf("/challenge") !== -1) { setNewsletterState("pending"); form.submit(); return; }
            throw new Error("newsletter_not_posted");
          })
          .catch(function () {
            if (button) { button.disabled = false; button.removeAttribute("aria-busy"); }
            if (errorEl) { errorEl.textContent = "That did not go through. Check the address and try again."; errorEl.hidden = false; }
          });
      });
    });
    /* Every `customer` form in this theme is a newsletter signup (footer,
       /pages/hello, the email band, the popup), so any landing with
       customer_posted=true is a completed signup -- not only the ajax
       challenge-page fallback that sets "pending" first. Mark it done
       unconditionally so the footer/hello plain-submit forms (which never
       run the ajax handler above, so state never becomes "pending") also
       suppress the popup. */
    if (window.location.search.indexOf("customer_posted=true") !== -1) {
      setNewsletterState("done");
    }
  }

  /* ---------- Homepage 2026-09-25: the exit-intent popup ----------
     Rules (snippets/newsletter-popup.liquid carries the reasoning):
     desktop = fine pointer and 56rem+: cursor leaves the top edge, after
     the delay; phone: second page of the session, after the delay; never
     on arrival, once per session, 30 days after a dismissal, never after
     a signup, never on basket/account/hello or when customer_posted. */
  function initNewsletterPopup() {
    var root = document.querySelector("[data-newsletter-popup]");
    if (!root) return;
    var path = window.location.pathname;
    if (/^\/(cart|account|pages\/hello)/.test(path)) return;
    if (window.location.search.indexOf("customer_posted=true") !== -1) return;
    var state = newsletterState();
    if (state === "done") return;
    if (state.indexOf("dismissed:") === 0) {
      var when = parseInt(state.slice(10), 10);
      if (when && Date.now() - when < 30 * 24 * 3600 * 1000) return;
    }
    var views = 1;
    try {
      if (sessionStorage.getItem("ostrich:popup-shown") === "1") return;
      views = parseInt(sessionStorage.getItem("ostrich:views") || "0", 10) + 1;
      sessionStorage.setItem("ostrich:views", String(views));
    } catch (e) {}

    var delayMs = (parseInt(root.getAttribute("data-popup-delay"), 10) || 8) * 1000;
    var armedAt = Date.now();
    var opened = false;
    var lastFocus = null;
    var isDesktop = window.matchMedia("(pointer: fine) and (min-width: 56rem)").matches;

    function open() {
      if (opened) return;
      opened = true;
      try { sessionStorage.setItem("ostrich:popup-shown", "1"); } catch (e) {}
      lastFocus = document.activeElement;
      root.hidden = false;
      document.body.classList.add("newsletter-popup-open");
      var input = root.querySelector("[data-newsletter-popup-input]");
      if (input) input.focus();
    }
    function close(dismissed) {
      root.hidden = true;
      document.body.classList.remove("newsletter-popup-open");
      if (dismissed && newsletterState() !== "done") setNewsletterState("dismissed:" + Date.now());
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    root.querySelectorAll("[data-newsletter-popup-close]").forEach(function (el) {
      el.addEventListener("click", function () { close(true); });
    });
    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && !root.hidden) close(true);
    });
    var form = root.querySelector("[data-newsletter-popup-form]");
    if (form) {
      form.addEventListener("submit", function () {
        /* the ajax handler shows the code in place; the popup then closes itself on the next close click */
      });
    }

    if (isDesktop) {
      document.addEventListener("mouseout", function (event) {
        if (event.relatedTarget || event.clientY > 0) return;
        if (Date.now() - armedAt < delayMs) return;
        open();
      });
    } else if (views >= 2) {
      setTimeout(open, delayMs);
    }
  }


  /* ---------- Live dispatch tag (homepage hero, announcement bar) ----------
     A "posted today" claim is only true before the cut-off on a working
     day. Elements carrying [data-dispatch-tag] are re-written every 30 s
     from settings.dispatch_cutoff_hour: before the cut-off, data-before
     with __TIME__ as the time left; after it (or at a weekend), data-after
     with __DAY__ as "tomorrow" or the weekday of the next posting day. The
     server-rendered text is the always-true form and stands when either
     template is blank. Same working-day arithmetic as initDeliveryDates. */
  var dispatchTagTickerArmed = false;
  function initDispatchTag() {
    var els = document.querySelectorAll("[data-dispatch-tag]");
    if (!els.length) return;
    var dayFmt;
    try { dayFmt = new Intl.DateTimeFormat("en-GB", { weekday: "long" }); } catch (e) { return; }
    function tick() {
      var now = new Date();
      els.forEach(function (el) {
        var cutoff = Number(el.getAttribute("data-cutoff-hour"));
        var before = el.getAttribute("data-before") || "";
        var after = el.getAttribute("data-after") || "";
        if (isNaN(cutoff)) return;
        var closed = !isDispatchDay(now);
        var todayCutoff = cutoffHourFor(now, cutoff);
        var pastCutoff = now.getHours() >= todayCutoff;
        if (!closed && !pastCutoff && before.indexOf("__TIME__") !== -1) {
          var cutoffAt = new Date(now.getTime()); cutoffAt.setHours(todayCutoff, 0, 0, 0);
          var minutes = Math.ceil((cutoffAt.getTime() - now.getTime()) / 60000);
          var h = Math.floor(minutes / 60), m = minutes % 60;
          var words = h > 0 ? h + (h > 1 ? " hrs " : " hr ") + m + " mins" : m + " mins";
          el.textContent = before.replace("__TIME__", words);
          return;
        }
        if (after.indexOf("__DAY__") === -1) return;
        var next = new Date(now.getTime()); next.setDate(next.getDate() + 1);
        next = nextDispatchDay(next);
        var tomorrow = new Date(now.getTime()); tomorrow.setDate(tomorrow.getDate() + 1);
        var word = next.toDateString() === tomorrow.toDateString() ? "tomorrow" : dayFmt.format(next);
        el.textContent = after.replace("__DAY__", word);
      });
    }
    tick();
    if (!dispatchTagTickerArmed) { dispatchTagTickerArmed = true; setInterval(tick, 30000); }
  }


  /* ---------- Live search: Shopify's Predictive Search (2026-09-25) ----------
     Alan: "would the search not be better as a live search? am sure shopify
     do that". Shopify's own /search/suggest, asked for the rendering of
     sections/predictive-search.liquid (section_id), so every product card
     is server-rendered through tier-guard: the rude cover, the price and
     the delivery date are the same as on any grid. Debounced, aborts the
     previous request, at least two characters, Escape closes, arrow keys
     walk the options, Enter on a highlighted option follows it, Enter
     otherwise submits the form to the search page exactly as before. */
  function initPredictiveSearch() {
    var strings = getOstrichStrings();
    document.querySelectorAll("[data-predictive-form]").forEach(function (form) {
      var input = form.querySelector("[data-predictive-input]");
      var box = form.querySelector("[data-predictive-results]");
      if (!input || !box) return;
      var controller = null, timer = null, lastQuery = "", active = -1;
      function items() { return Array.prototype.slice.call(box.querySelectorAll("[data-predictive-item]")); }
      function close() {
        box.hidden = true; input.setAttribute("aria-expanded", "false"); active = -1;
      }
      function open() {
        box.hidden = false; input.setAttribute("aria-expanded", "true");
        initCardMedia(box); initCardMask(box); initDeliveryDates();
      }
      function highlight(n) {
        var list = items();
        list.forEach(function (el) { el.removeAttribute("aria-selected"); var li = el.closest("li"); if (li) li.removeAttribute("aria-selected"); });
        active = n;
        if (n < 0 || n >= list.length) { active = -1; return; }
        var el = list[n]; el.setAttribute("aria-selected", "true"); var li = el.closest("li"); if (li) li.setAttribute("aria-selected", "true");
        if (el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
      }
      function fetchNow() {
        var q = input.value.trim();
        if (q.length < 2) { close(); return; }
        if (q === lastQuery && !box.hidden) return;
        if (controller) controller.abort();
        controller = new AbortController();
        var url = (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : "/") +
          "search/suggest?q=" + encodeURIComponent(q) +
          "&resources[type]=product,collection,query&resources[limit]=6&resources[limit_scope]=each&resources[options][unavailable_products]=hide&section_id=predictive-search";
        fetch(url, { signal: controller.signal, headers: { "Accept": "text/html" } })
          .then(function (r) { if (!r.ok) throw new Error("predictive_" + r.status); return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, "text/html");
            var panel = doc.querySelector("#shopify-section-predictive-search") || doc.body;
            var inner = panel.querySelector(".predictive__panel");
            if (!inner) { close(); return; }
            box.innerHTML = "";
            box.appendChild(inner);
            lastQuery = q;
            open();
          })
          .catch(function (err) { if (err && err.name === "AbortError") return; close(); });
      }
      input.addEventListener("input", function () {
        if (timer) clearTimeout(timer);
        timer = setTimeout(fetchNow, 220);
      });
      input.addEventListener("focus", function () { if (input.value.trim().length >= 2 && lastQuery === input.value.trim()) open(); });
      input.addEventListener("keydown", function (e) {
        if (box.hidden) return;
        var list = items();
        if (e.key === "ArrowDown") { e.preventDefault(); highlight(active + 1 >= list.length ? 0 : active + 1); }
        else if (e.key === "ArrowUp") { e.preventDefault(); highlight(active - 1 < 0 ? list.length - 1 : active - 1); }
        else if (e.key === "Escape") { close(); }
        else if (e.key === "Enter" && active >= 0 && list[active]) { e.preventDefault(); window.location.href = list[active].getAttribute("href"); }
      });
      document.addEventListener("click", function (e) { if (!form.contains(e.target)) close(); });
      form.addEventListener("submit", function () { close(); });
    });
  }

  document.addEventListener("DOMContentLoaded", function () {
    [
      ["mobileNav", "[data-mobile-nav-toggle]", initMobileNav],
      ["toneSwitchOff", 'html[data-tone-switch="off"]', enforceToneSwitchOff],
      ["rudeSwitch", "[data-rude-switch]", initRudeSwitch],
      ["deliveryDates", "[data-delivery-date]", initDeliveryDates],
      ["personalisationPreview", "[data-personalisation-input]", initPersonalisationPreview],
    ["livePreview", "[data-live-preview]", initLivePreview],
      ["navDropdowns", ".site-header__nav [data-nav-dropdown]", initNavDropdowns],
      ["facets", "[data-facets-form]", initFacets],
      ["searchResultsEnhance", "#AiSearchInline[data-initial-query]", initSearchResultsEnhance],
      ["searchFieldReset", 'form[action$="/search"] input[name="q"]', initSearchFieldReset],
      ["cardMedia", ".product-card__media", initCardMedia],
      ["cardSpinHover", ".product-card[data-spin]", initCardSpinHover],
      ["cardMask", '.product-card[data-tier="rude"]', initCardMask],
      ["rudeSwitchFloating", "[data-rude-switch-floating]", updateFloatingRudeSwitchVisibility],
      ["gallery", "[data-gallery]", initGallery],
      ["quantity", "[data-qty-field]", initQuantity],
      ["variantPicker", "[data-product-form]", initVariantPicker],
      ["personalisationUploads", '[data-personalisation-kind="photo"]', initPersonalisationUploads],
      ["personalisationReason", "[data-personalisation-reason]", initPersonalisationReason],
      ["stickyBuyBar", "[data-sticky-buy-bar]", initStickyBuyBar],
      ["cartDrawer", "[data-cart-drawer]", initCartDrawer],
      ["cartForm", "[data-cart-form]", initCartForm],
      ["recommendations", "[data-recommendations]", initRecommendations],
      ["heroCarousel", "[data-hero]", initHeroCarousel],
      ["wallReveal", "[data-wall-reveal] .product-card", initWallReveal],
      ["helloOffer", "[data-hello-offer]", initHelloOffer],
      ["heroFinder", "[data-finder]", initHeroFinder],
      ["newsletterAjax", "[data-newsletter-ajax]", initNewsletterAjax],
      ["newsletterPopup", "[data-newsletter-popup]", initNewsletterPopup],
      ["dispatchTag", "[data-dispatch-tag]", initDispatchTag],
      ["wallClock", "[data-wall-clock]", initWallClock],
      ["fridgeNote", "[data-fridge-note]", initFridgeNote],
      ["predictiveSearch", "[data-predictive-form]", initPredictiveSearch]
    ].forEach(function (entry) {
      var name = entry[0], selector = entry[1], fn = entry[2];
      try {
        if (selector && !document.querySelector(selector)) return;
        fn();
      } catch (err) {
        if (window.console && console.error) {
          console.error("[ostrich] init " + name + " failed:", err);
        }
      }
    });
  });
})();
